import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  afflictionsOf, CONDITIONS, combatantOf, curableBy, forecast, isHostile, itemHealFor, itemUseOf,
  livingUnits,
  MAX_INVENTORY,
  phaseIsOver, samePosition, serialize, spanDistance, terrainAt, unitAt, unitById, weaponOf,
  type Affliction, type AiDifficulty, type Content, type Event, type ItemInstance, type MatchState,
  type Stats, type Unit, type Vec2,
} from '@oz/rules';
import { conditionLabel, conditionList, describeUse, slotView } from '../game/items.js';
import {
  AUTO_SLOT, deleteSave, listSaves, readSave, restoreState, saveLabel,
  saveWhen, writeSave, SAVE_SLOTS,
  type SaveGame, type SlotId,
} from '../game/saves.js';
import {
  ASSETS_BASE, battlerArt, frameRect, loadManifest, type Manifest,
} from '../assets/manifest.js';
import { Board } from '../game/board.js';
import { GameAudio } from '../game/audio.js';
import {
  buildSkirmish, loadRoster, loadSkirmishContent, personalSquad, saveRoster, toGameMap,
  type RosterUnit, type SkirmishContent, type UnitPresentation,
} from '../game/setup.js';
import { useSkirmish } from '../game/useSkirmish.js';
import {
  loadConfirmButton,
  saveConfirmButton,
  type ConfirmButton,
} from '../game/useGamepad.js';
import {
  cycleZoom,
  stepZoom,
  zoomLevel,
  zoomScale,
  DEFAULT_ZOOM,
  type ZoomId,
} from '../render/zoom.js';
import { useGamepad } from '../game/useGamepad.js';
import { matchSeed, onlineWinner, type OnlineLobbyRef } from '../game/online.js';
import { useAuth } from '../auth/AuthContext.js';
import { CombatScene } from './CombatScene.js';
import type { MapData } from '../render/tilemap.js';
import { objectiveLabel, type Scene } from '../game/story.js';
import { CutscenePlayer } from '../game/CutscenePlayer.js';
import { type Chapter } from './CampaignPage.js';
import { TitleScreen } from './TitleScreen.js';
import { PhaseBanner } from '../game/PhaseBanner.js';
import { ItemIcon } from '../components/ItemIcon.js';
import { acquireWakeLock, releaseWakeLock } from '../game/wakeLock.js';
import { useDeviceType, lockLandscapeOrientation, unlockOrientation } from '../game/device.js';
import { VirtualTouchControls } from '../components/VirtualTouchControls.js';

const DEFAULT_MAP_ID = 13; // Oásis — 25×25, com água como obstáculo natural.

/**
 * A trilha quando o mapa não declara nenhuma.
 *
 * O pipeline preenche a faixa de todos os mapas do acervo; isto cobre o mapa
 * em rascunho, que sai do editor sem passar por lá.
 */
const FALLBACK_BGM = 'Field1';

interface MapOption {
  readonly id: number;
  readonly name: string;
  /** Largura e altura em casas — o que o seletor mostra debaixo do nome. */
  readonly width?: number | undefined;
  readonly height?: number | undefined;
  readonly isCustom?: boolean | undefined;
  /** De qual jogo importado o mapa veio, quando veio de um. */
  readonly pack?: string | null | undefined;
}

/**
 * Retrato recortado da folha de faces do acervo.
 *
 * O recorte é feito em porcentagem, e não em pixels: o quadro cresce e encolhe
 * com a ficha — que é medida em `em` e acompanha o tamanho do campo — e um
 * recorte em pixels ficaria para trás, mostrando o rosto do vizinho pela
 * metade. Com a folha esticada para (colunas × 100%) e a posição em fração da
 * grade, o recorte é sempre o quadro certo, de qualquer tamanho.
 */
function Portrait({
  manifest, sheet, index,
}: { manifest: Manifest; sheet: string; index: number }): JSX.Element | null {
  const entry = manifest.images['faces']?.[sheet];
  if (!entry?.grid) return null;

  // Contadas da imagem, e não da grade declarada: uma folha com uma fileira a
  // mais do que o acervo anota recortaria torto.
  const columns = Math.max(1, Math.round(entry.width / entry.grid.frameWidth));
  const rows = Math.max(1, Math.round(entry.height / entry.grid.frameHeight));
  const column = index % columns;
  const row = Math.floor(index / columns);

  return (
    <div
      className="portrait"
      style={{
        backgroundImage: `url(${ASSETS_BASE}/img/faces/${entry.file})`,
        backgroundSize: `${columns * 100}% ${rows * 100}%`,
        backgroundPosition: `${columns > 1 ? (column / (columns - 1)) * 100 : 0}% ${
          rows > 1 ? (row / (rows - 1)) * 100 : 0
        }%`,
      }}
    />
  );
}

/**
 * O retrato de quem não tem rosto no acervo.
 *
 * As folhas de retrato são de gente; um goblin, um dragão ou um esqueleto não
 * estão em nenhuma delas. A ficha caía num rosto genérico qualquer e o jogador
 * inspecionava um dragão para ver a cara de um aldeão. O desenho de combate é
 * o retrato que a criatura tem — e é o mesmo que ela vai mostrar no duelo.
 */
function BattlerPortrait({
  manifest, battler,
}: { manifest: Manifest; battler: string }): JSX.Element | null {
  const art = battlerArt(manifest, battler);
  if (!art) return null;

  return (
    <div className="portrait portrait-battler">
      <img src={art.url} alt="" />
    </div>
  );
}

/** O sinal de cada condição no diário de batalha. */
const CONDITION_MARK: Readonly<Record<keyof typeof CONDITIONS, string>> = {
  envenenado: '☠', queimando: '🔥', paralisado: '⚡', adormecido: '💤', cego: '🌑', silenciado: '🔇',
};

/** "deixa de estar envenenado", "livra-se de todas as condições". */
function curedPhrase(ids: readonly (keyof typeof CONDITIONS)[]): string {
  const list = conditionList(ids);
  return list === 'todas as condições' ? 'livra-se de todas as condições' : `deixa de estar ${list.toLowerCase()}`;
}

/** "envenena 50% · cega 10%" — o que um golpe certeiro pode causar. */
const AFFLICTION_VERB: Readonly<Record<keyof typeof CONDITIONS, string>> = {
  envenenado: 'envenena', queimando: 'queima', paralisado: 'paralisa',
  adormecido: 'faz dormir', cego: 'cega', silenciado: 'silencia',
};

function afflictionText(list: readonly Affliction[] | undefined): string {
  return (list ?? []).map((a) => `${AFFLICTION_VERB[a.condition]} ${a.chance}%`).join(' · ');
}

/**
 * As condições de uma unidade, com o prazo: ícone, nome e turnos.
 *
 * Zero turnos é "último turno" — a condição ainda vale até o fim da fase da
 * unidade, e mostrar "0" daria a entender que ela já saiu.
 */
function ConditionChips({ unit }: { unit: Unit }): JSX.Element | null {
  if (unit.states.length === 0) return null;
  return (
    <ul className="condition-chips" aria-label="Condições">
      {unit.states.map((s) => {
        const def = CONDITIONS[s.condition];
        const left = s.turnsLeft === null ? 'até curar' : s.turnsLeft === 0 ? 'último turno' : `${s.turnsLeft}t`;
        return (
          <li key={s.condition} className={`condition-chip condition-${s.condition}`} title={def.summary}>
            <ItemIcon icon={def.icon} size="1.2em" />
            <span className="condition-chip-name">{def.label}</span>
            <span className="condition-chip-turns">{left}</span>
          </li>
        );
      })}
    </ul>
  );
}

function HpBar({ current, max, team }: { current: number; max: number; team: string }): JSX.Element {
  const ratio = Math.max(0, Math.min(1, current / max));
  return (
    <div className="hpbar" role="img" aria-label={`${current} de ${max} pontos de vida`}>
      <span style={{ width: `${ratio * 100}%` }} data-team={team} />
      <b>{current}<i>/{max}</i></b>
    </div>
  );
}

/**
 * O seletor de mapas das opções da partida.
 *
 * O `<select>` nativo não servia: são mais de cem mapas entre o acervo, os
 * jogos importados e o editor, e a lista do sistema operacional os despeja num
 * fio único de texto — sem busca, sem tamanho, sem dizer de qual jogo cada um
 * veio, e com uma aparência que não é a do resto da tela. Aqui a mesma escolha
 * vira um painel próprio: um campo de busca, cartões com o nome, a medida em
 * casas e a procedência, e as setas de sempre para folhear um a um no controle.
 */
function MapPicker({
  maps, value, onChange,
}: {
  maps: readonly MapOption[];
  value: number;
  onChange: (id: number) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = maps.find((m) => m.id === value);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return maps;
    return maps.filter(
      (m) =>
        m.name.toLowerCase().includes(needle) ||
        String(m.id).includes(needle) ||
        (m.pack ?? '').toLowerCase().includes(needle),
    );
  }, [maps, query]);

  /*
   * A lista abre já mostrando o mapa em uso.
   *
   * Rolada à mão, e não com `scrollIntoView`: aquele rola TODOS os ancestrais
   * que rolam, e o corpo do diálogo é um deles — abrir a lista arrastava o
   * diálogo inteiro junto. Aqui só a lista se move.
   */
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>('[data-current="true"]');
    if (list && row) {
      list.scrollTop = row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2;
    }

    // E o corpo do diálogo rola até o painel: abrir uma lista que nasce fora da
    // parte visível é o mesmo que não abrir nada.
    const panel = panelRef.current;
    const body = panel?.closest<HTMLElement>('.options-modal-body');
    if (panel && body) {
      const overflow = panel.getBoundingClientRect().bottom - body.getBoundingClientRect().bottom;
      if (overflow > 0) body.scrollTop += overflow + 8;
    }
  }, [open]);

  const step = (delta: number): void => {
    if (maps.length <= 1) return;
    const index = maps.findIndex((m) => m.id === value);
    const next = maps[((index >= 0 ? index : 0) + delta + maps.length) % maps.length];
    if (next) onChange(next.id);
  };

  const label = (map: MapOption): string =>
    map.width && map.height ? `${map.width}×${map.height} casas` : 'medida desconhecida';

  return (
    <div className={`mappicker ${open ? 'mappicker-open' : ''}`}>
      <div className="mappicker-bar">
        <button
          type="button"
          className="mappicker-step"
          onClick={() => step(-1)}
          title="Mapa anterior (◀ / seta esquerda)"
          aria-label="Mapa anterior"
        >
          ‹
        </button>

        <button
          type="button"
          className="mappicker-current"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span className="mappicker-current-main">
            <span className="mappicker-id">#{value}</span>
            <span className="mappicker-name">{current?.name ?? 'Mapa desconhecido'}</span>
          </span>
          <span className="mappicker-current-sub">
            {current ? label(current) : '—'}
            {current?.isCustom && <span className="mappicker-tag custom">editor</span>}
            {current?.pack && <span className="mappicker-tag pack">{current.pack}</span>}
          </span>
          <span className="mappicker-caret" aria-hidden="true">▾</span>
        </button>

        <button
          type="button"
          className="mappicker-step"
          onClick={() => step(1)}
          title="Próximo mapa (▶ / seta direita)"
          aria-label="Próximo mapa"
        >
          ›
        </button>
      </div>

      {open && (
        <div className="mappicker-panel" ref={panelRef}>
          <input
            type="search"
            className="mappicker-search"
            placeholder="Procurar mapa por nome, número ou jogo…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="mappicker-list" ref={listRef} role="listbox">
            {matches.length === 0 && (
              <p className="mappicker-empty">Nenhum mapa com “{query}”.</p>
            )}
            {matches.map((m) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={m.id === value}
                data-current={m.id === value}
                className={`mappicker-row ${m.id === value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                }}
              >
                <span className="mappicker-row-id">#{m.id}</span>
                <span className="mappicker-row-body">
                  <span className="mappicker-row-name">{m.name}</span>
                  <span className="mappicker-row-meta">
                    {label(m)}
                    {m.isCustom && <span className="mappicker-tag custom">editor</span>}
                    {m.pack && <span className="mappicker-tag pack">{m.pack}</span>}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A fala de quem acabou de mudar de lado.
 *
 * Componente, e não um trecho solto de JSX, porque enquanto ela está no ar o
 * controle da batalha está desligado (`modalBlocksKeysRef`) — e ela não tinha
 * nenhum no lugar. Quem jogava de joystick via a conversa aparecer e ficava sem
 * como fechá-la: a única saída era o mouse. Fechar é o teclado de sempre e
 * qualquer botão do controle, porque não há nada aqui a escolher.
 */
function RecruitScene({
  name,
  text,
  onClose,
  confirmButton,
}: {
  readonly name: string;
  readonly text: string;
  readonly onClose: () => void;
  readonly confirmButton: ConfirmButton;
}): JSX.Element {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (['Enter', ' ', 'Escape', 'z', 'x'].includes(event.key)) {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useGamepad({ onAnyFaceButton: onClose, onEndPhase: onClose }, true, confirmButton);

  return (
    <div
      className="recruit-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`${name} entra para o esquadrão`}
      onClick={onClose}
    >
      <div className="window recruit-modal">
        <p className="recruit-title">💬 {name} muda de lado</p>
        <p className="recruit-body">{text}</p>
        <button type="button" className="recruit-ok" onClick={onClose}>
          Continuar
        </button>
      </div>
    </div>
  );
}

export function Skirmish({
  initialMapId,
  draftMap,
  storyChapter,
  onlineLobby,
  onExitToCampaign,
  resumeSave: resumeSaveProp,
}: {
  initialMapId?: number | undefined;
  /** Mapa vindo do editor que ainda não foi (ou não pôde ser) gravado. */
  draftMap?: MapData | undefined;
  /*
   * De onde esta batalha veio, quando não veio de lugar nenhum.
   *
   * A escaramuça não sabe o que é campanha nem o que é sala online — ela sabe
   * jogar um mapa, e é bom que continue assim. Estes dois campos são só o
   * bilhete de volta: ao terminar, o desfecho é contado a quem mandou a
   * batalha, e é de lá que vêm a recompensa e o próximo capítulo.
   */
  storyChapter?: Chapter | null;
  onlineLobby?: OnlineLobbyRef | null;
  /**
   * Volta para a lista de capítulos.
   *
   * A batalha não sabe trocar de aba — quem sabe é quem a montou. Sem este
   * bilhete de volta, a única saída da tela de fim de capítulo era o menu do
   * topo, e quem jogava em tela cheia não tinha saída nenhuma.
   */
  onExitToCampaign?: (() => void) | undefined;
  /**
   * Uma partida gravada para retomar em vez de montar uma nova.
   *
   * Chega de fora quando a lista de capítulos oferece "Continuar": a tela de
   * abertura da Escaramuça tem a própria lista, mas quem está no Modo História
   * não deveria ter de passar por ela para voltar à batalha que deixou pela
   * metade.
   */
  resumeSave?: SaveGame | null | undefined;
} = {}): JSX.Element {
  /*
   * O capítulo em curso vira ESTADO, e não só propriedade.
   *
   * Porque a tela passou a poder trocá-lo por conta própria: ao vencer um
   * capítulo ela oferece o seguinte, e ao retomar uma partida gravada ela
   * reabre o capítulo de onde a gravação veio. Antes, o único jeito de mudar
   * de capítulo era voltar à aba História e escolher outro na lista — e o
   * jogador terminava a batalha numa tela de vitória que não sabia o que era
   * uma campanha.
   */
  const [chapter, setChapter] = useState<Chapter | null>(storyChapter ?? null);
  useEffect(() => {
    setChapter(storyChapter ?? null);
  }, [storyChapter]);

  /*
   * A tela de abertura, e o que a pula.
   *
   * Abrir a aba deixou de ser começar a jogar. Mas quem chega com uma batalha
   * já escolhida — um capítulo da campanha, uma sala online, o mapa que o
   * editor mandou testar — não passa pelo menu: pedir "Novo jogo" a quem
   * acabou de apertar "Jogar este mapa" seria perguntar duas vezes a mesma
   * coisa.
   */
  const [screen, setScreen] = useState<'titulo' | 'batalha'>(() =>
    storyChapter || onlineLobby || draftMap || initialMapId ? 'batalha' : 'titulo',
  );
  // Os atalhos de teclado são montados uma vez e não enxergam o estado que
  // muda; esta referência é como eles sabem em que tela a pessoa está.
  const screenRef = useRef(screen);
  screenRef.current = screen;
  useEffect(() => {
    if (storyChapter || onlineLobby || draftMap || initialMapId) setScreen('batalha');
  }, [storyChapter, onlineLobby, draftMap, initialMapId]);

  /*
   * A gravação que chegou de fora entra em cena.
   *
   * O capítulo vem pela propriedade de sempre; o que falta aqui é o estado do
   * tabuleiro — a semente, a dificuldade e as unidades onde elas pararam.
   */
  const [restoring, setRestoring] = useState<SaveGame | null>(null);
  useEffect(() => {
    if (!resumeSaveProp) return;
    setRestoring(resumeSaveProp);
    setSeed(resumeSaveProp.seed);
    setDifficulty(resumeSaveProp.difficulty);
    setScreen('batalha');
  }, [resumeSaveProp]);

  const hostRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<Board | null>(null);
  const audioRef = useRef<GameAudio | null>(null);

  const [mapId, setMapId] = useState(initialMapId ?? DEFAULT_MAP_ID);
  const [mapList, setMapList] = useState<readonly MapOption[]>([]);
  // O rascunho vindo do editor vira estado local para que escolher outro mapa
  // no seletor o descarte — senão a seleção mudaria e o mapa continuaria o
  // mesmo, porque o rascunho tem prioridade no carregamento.
  const [activeDraft, setActiveDraft] = useState<MapData | undefined>(draftMap);

  useEffect(() => {
    setActiveDraft(draftMap);
  }, [draftMap]);

  useEffect(() => {
    if (initialMapId) {
      setMapId(initialMapId);
    }
  }, [initialMapId]);
  const [loaded, setLoaded] = useState<SkirmishContent | null>(null);
  // O diário de batalha precisa do acervo para nomear o item que a unidade
  // bebeu, e ele é montado num callback estável — daí a referência.
  const loadedRef = useRef<SkirmishContent | null>(null);
  loadedRef.current = loaded;
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState('');
  const [muted, setMuted] = useState(false);
  const battlefieldRef = useRef<HTMLDivElement>(null);
  const device = useDeviceType();
  const [fullscreen, setFullscreen] = useState(false);
  /*
   * O afastamento da câmera, em três posições fixas — o esquema dos Fire Emblem
   * de GameCube. Fica aqui, e não no Board, porque a interface precisa saber
   * qual está em uso para acender o botão certo.
   */
  const [zoom, setZoom] = useState<ZoomId>(DEFAULT_ZOOM);
  /*
   * Qual botão do controle confirma.
   *
   * Isto era deduzido do nome do controle, e errava: um adaptador genérico que
   * se anuncia como Nintendo trocava confirmar com cancelar, e a partida ficava
   * impossível de jogar sem que nada na tela explicasse por quê. Agora é
   * escolha do jogador, lembrada entre sessões.
   */
  const [confirmButton, setConfirmButton] = useState<ConfirmButton>(loadConfirmButton);
  // Os atalhos de teclado e o controle são montados uma vez só e não enxergam o
  // estado que muda; esta referência é como eles leem o afastamento atual.
  const zoomRef = useRef<ZoomId>(zoom);
  zoomRef.current = zoom;
  // Só para a interface saber se já há som; o estado de verdade mora no GameAudio.
  const [audioReady, setAudioReady] = useState(false);
  // O tabuleiro sobe de forma assíncrona; sem este sinal, o cursor e as
  // unidades já teriam sido empurrados para um Board que ainda não existia, e a
  // câmera abriria no canto errado do mapa.
  const [boardReady, setBoardReady] = useState(0);
  // Sobe quando o navegador derruba o WebGL do tabuleiro: um tabuleiro novo é
  // montado no lugar, e a partida segue de onde estava.
  const [boardEpoch, setBoardEpoch] = useState(0);
  const [notice, setNotice] = useState('');
  const [log, setLog] = useState<readonly string[]>([]);
  const [activeCombat, setActiveCombat] = useState<{
    event: Extract<Event, { type: 'combate' }>;
    before: MatchState;
    resolve: () => void;
  } | null>(null);

  /*
   * A semente de cada escaramuça, sorteada de novo a cada partida.
   *
   * Era uma constante no código — e uma constante sorteia sempre o mesmo
   * elenco. Abrir qualquer mapa trazia os mesmos quatro heróis contra os
   * mesmos quatro monstros, nas mesmas casas: a escaramuça é uma partida de
   * TESTE, e testar sempre o mesmo confronto não testa nada.
   *
   * Recomeçar a partida sorteia outra; trocar de mapa também. O que NÃO
   * sorteia é o React remontar o componente — daí ser estado, e não uma
   * expressão avaliada no corpo da função.
   */
  // No online, a semente é a da sala: os dois jogadores abrem o MESMO tabuleiro.
  const [seed, setSeed] = useState(() => matchSeed(onlineLobby, Math.random));
  /*
   * A dificuldade mora AQUI, e não dentro do gancho da partida.
   *
   * Ela deixou de ser só o juízo da IA: decide também quantos inimigos entram
   * em campo e de onde eles saem — escolhas feitas ao montar o tabuleiro, antes
   * de a partida existir. Mudá-la remonta a escaramuça, que é o comportamento
   * certo: não dá para acrescentar três inimigos no meio do turno.
   */
  // No capítulo, a dificuldade sugerida é a dele; o jogador ainda pode trocar.
  const [difficulty, setDifficulty] = useState<AiDifficulty>(() => chapter?.difficulty ?? 'normal');
  /*
   * De onde esta batalha veio decide três regras de uma vez: quem é o
   * esquadrão, se alguém sobe de nível e o que sobra registrado no fim.
   */
  const matchMode: 'escaramuca' | 'campanha' | 'online' = chapter
    ? 'campanha'
    : onlineLobby
      ? 'online'
      : 'escaramuca';

  /*
   * O esquadrão guardado do jogador — a memória da campanha.
   *
   * A campanha e o desafio online levam OS personagens do jogador, no nível em
   * que a campanha os deixou; a escaramuça não passa por aqui, porque sorteia
   * o elenco. `null` enquanto carrega, para a partida não ser montada com o
   * esquadrão errado e trocada um instante depois.
   */
  const [roster, setRoster] = useState<readonly RosterUnit[] | null>(
    matchMode === 'escaramuca' ? [] : null,
  );

  /**
   * A gravação do capítulo que acabou, enquanto ela ainda está no ar.
   *
   * O fim de um capítulo dispara duas escritas ao servidor sem esperar por
   * elas — o esquadrão que subiu de nível e a conclusão que destranca o
   * recruta. Reler o esquadrão antes de a primeira pousar traria os níveis do
   * capítulo anterior. Guardar a promessa aqui é o que permite ao capítulo
   * seguinte esperar por ela sem prender a tela de desfecho.
   */
  const rosterWriteRef = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    if (matchMode === 'escaramuca') {
      setRoster([]);
      return;
    }
    let cancelled = false;
    /*
     * CADA capítulo relê o esquadrão — este era o buraco.
     *
     * A lista era buscada uma vez, quando a tela subia, e "Próximo capítulo"
     * não desmonta a tela: troca o capítulo, o mapa e a semente por dentro.
     * Então o capítulo seguinte era montado com o esquadrão de ANTES da
     * vitória — e quem o servidor tinha acabado de destrancar não existia para
     * o cliente. Era por isso que Lucretia, recrutada no Capítulo 1, não
     * entrava no Capítulo 2: ela estava no banco, com nível e bolsa, e o
     * `personalSquad` a descartava por não achá-la na lista em memória.
     *
     * Voltar a `null` não é detalhe: é o que faz a partida ESPERAR (ver a
     * guarda em `skirmish`). Sem isso o tabuleiro nasceria com o esquadrão
     * velho e seria trocado um instante depois.
     */
    setRoster(null);
    void rosterWriteRef.current
      // O que falhou ao gravar não pode impedir a leitura: pior do que abrir
      // com um nível atrasado é não abrir.
      .catch(() => undefined)
      .then(() => loadRoster())
      .then((units) => {
        if (!cancelled) setRoster(units);
      });
    return () => {
      cancelled = true;
    };
  }, [matchMode, chapter?.id]);
  const reseed = useCallback(() => setSeed(Math.floor(Math.random() * 0x7fffffff)), []);
  /*
   * Mudar a dificuldade remonta o tabuleiro — e por isso descarta a gravação
   * que estava sendo retomada.
   *
   * Sem isto a partida remontada voltaria ao ponto gravado com a dificuldade
   * nova, o que não é nem a partida guardada nem uma partida nova.
   */
  const changeDifficulty = useCallback((next: AiDifficulty) => {
    setRestoring(null);
    setDifficulty(next);
  }, []);
  /*
   * A régua com que a partida mede os monstros.
   *
   * Quantas casas um bicho ocupa sai do tamanho do desenho dele, e quem sabe
   * medir imagem é o manifesto. A escaramuça não conhece o acervo — recebe a
   * régua pronta.
   */
  const measureBattler = useCallback(
    (battler: string) => {
      if (!manifest) return null;
      const art = battlerArt(manifest, battler);
      return art ? { width: art.entry.width, height: art.entry.height } : null;
    },
    [manifest],
  );
  /*
   * `restoring` é a partida gravada que está sendo retomada.
   *
   * Ela não vira tabuleiro na hora de ser escolhida: o mapa vem do servidor, e
   * até ele chegar não há onde pôr as unidades. Fica guardada até o acervo do
   * mapa certo estar em mãos, e então o tabuleiro é montado a partir dela em
   * vez de ser sorteado.
   */
  const skirmish = useMemo(
    () => {
      if (!loaded || !manifest || roster === null) return null;

      /*
       * O acervo em mãos ainda é o do mapa ANTERIOR: espera.
       *
       * Trocar de mapa muda `mapId` num instante e `loaded` só quando o
       * servidor responde. Sem esta guarda, o intervalo entre os dois montava
       * uma partida inteira no mapa velho — e era isso que se via ao escolher
       * "Próximo capítulo": o capítulo seguinte abria recomeçando o mapa que o
       * jogador acabara de vencer, e só depois pulava para o certo. O rascunho
       * do editor não passa por aqui: ele não vem do servidor e pode nem ter id.
       */
      if (!activeDraft && loaded.map.id !== mapId) return null;

      if (restoring && restoring.mapId === loaded.map.id) {
        return {
          state: restoreState(restoring, toGameMap(loaded.map)),
          presentation: new Map(restoring.presentation),
        };
      }

      return buildSkirmish(loaded, seed, measureBattler, {
            difficulty,
            /*
             * Quem entra em campo depende de onde a batalha veio.
             *
             * A escaramuça sorteia — é teste. A campanha e o online levam o
             * esquadrão do jogador, porque é dele que a partida trata: no
             * online os dois lados jogam com o que conquistaram, e é isso que
             * faz o desafio valer.
             */
            ...(matchMode === 'escaramuca'
              ? {}
              : {
                  squad: personalSquad(
                    loaded,
                    roster,
                    chapter?.allowedCharacters?.length ? chapter.allowedCharacters.length : 4,
                    chapter?.allowedCharacters ?? undefined,
                  ),
                }),
            ...(chapter?.enemyLevel ? { enemyLevel: chapter.enemyLevel } : {}),
            // O objetivo do capítulo vence o do mapa — antes ele era gravado e
            // mostrado, e a partida jogava com o do mapa.
            ...(chapter?.objective ? { objective: chapter.objective } : {}),
            /*
             * A escaramuça não faz ninguém subir de nível.
             *
             * Ela é uma partida de teste com esquadrão sorteado: o que
             * acontece aqui não pode mexer na ficha que a campanha guarda, e
             * um teste que altera o estado do jogador deixa de ser repetível.
             * Campanha e online passam a sua própria regra.
             */
            /*
             * E na campanha só o SEU lado sobe de nível.
             *
             * Do outro lado está a máquina: um exército que cresce enquanto o
             * jogador pensa transforma cada turno gasto em punição, e o
             * capítulo termina mais difícil do que o autor o desenhou.
             */
            progression: matchMode === 'campanha' ? ('jogador' as const) : false,
            // Quem, do lado de lá, muda de lado se falarem com ele.
            ...(chapter?.recruits?.length ? { recruits: chapter.recruits } : {}),
            /*
             * E o capítulo abastece o mapa.
             *
             * Só ele: a escaramuça é teste e o desafio online premiaria quem
             * calhasse de nascer perto do baú. Ver `supplyChests` — a poção da
             * bolsa inicial não atravessa um capítulo inteiro, e os mapas do
             * acervo não trazem tesouro anotado nenhum.
             */
            ...(matchMode === 'campanha' ? { supplies: true } : {}),
          });
    },
    [loaded, manifest, measureBattler, seed, difficulty, matchMode, roster, chapter, restoring, activeDraft, mapId],
  );
  const presentation = skirmish?.presentation ?? new Map<number, UnitPresentation>();

  // --- carregamento de mapas e acervo ---------------------------------------

  useEffect(() => {
    fetch('/api/maps')
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as {
          maps: Array<{
            id: number; name: string; width?: number; height?: number;
            isCustom?: boolean; pack?: string | null;
          }>;
        };
        // O rótulo "[Custom]" saiu do nome: quem é do editor agora se diz numa
        // etiqueta própria, e o nome fica sendo só o nome — que é o que a busca
        // do seletor procura.
        return data.maps.map((m) => ({
          id: m.id,
          name: m.name,
          width: m.width,
          height: m.height,
          isCustom: Boolean(m.isCustom),
          pack: m.pack ?? null,
        }));
      })
      .catch(() =>
        fetch(`${ASSETS_BASE}/maps/index.json`).then((r) => r.json() as Promise<MapOption[]>),
      )
      .then((list) => setMapList(list))
      .catch(() => {});
  }, []);

  /*
   * O mapa de abertura da escaramuça, quando o de sempre já não existe.
   *
   * `DEFAULT_MAP_ID` é um id fixo, e um administrador pode excluir qualquer
   * mapa do acervo — inclusive esse. Sem isto, abrir a aba depois da exclusão
   * batia num erro de carregamento e a tela não servia para mais nada. Vale só
   * para o mapa PADRÃO: quem chegou com um mapa escolhido (capítulo, sala,
   * rascunho do editor) tem de receber esse, ou um aviso.
   */
  useEffect(() => {
    if (initialMapId !== undefined || storyChapter || onlineLobby || activeDraft) return;
    if (mapList.length === 0 || mapList.some((m) => m.id === mapId)) return;
    setMapId(mapList[0]!.id);
  }, [mapList, mapId, initialMapId, storyChapter, onlineLobby, activeDraft]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      manifest ? Promise.resolve(manifest) : loadManifest(),
      loadSkirmishContent(mapId, activeDraft),
    ])
      .then(([m, content]) => {
        if (cancelled) return;
        setManifest(m);
        setLoaded(content);
        if (!audioRef.current) {
          audioRef.current = new GameAudio(m);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [mapId, activeDraft]);

  useEffect(() => {
    const unlock = () => audioRef.current?.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // --- eventos do motor -> som e diário -------------------------------------

  const describe = useCallback(
    (state: MatchState, event: Event): string | null => {
      const name = (id: number): string => unitById(state, id)?.name ?? `#${id}`;
      const audio = audioRef.current;

      switch (event.type) {
        case 'moveu':
          return null;
        case 'esperou':
          return `${name(event.unitId)} mantém a posição.`;
        case 'combate': {
          const lines: string[] = [];
          for (const blow of event.blows) {
            const who = blow.side === 'atacante' ? name(event.attackerId) : name(event.defenderId);
            const alvo = blow.side === 'atacante' ? name(event.defenderId) : name(event.attackerId);
            if (blow.result === 'erro') {
              audio?.play('falha');
              lines.push(`${who} erra o golpe em ${alvo}.`);
            } else {
              audio?.play(blow.result === 'critico' ? 'critico' : 'acerto');
              lines.push(
                `${who} acerta ${alvo}: ${blow.damage} de dano${blow.result === 'critico' ? ' — CRÍTICO!' : ''}.`,
              );
            }
          }
          return lines.join(' ');
        }
        case 'curou': {
          audio?.play('cura');
          const healed = event.amount > 0
            ? `${name(event.healerId)} cura ${name(event.targetId)} em ${event.amount} PV`
            : `${name(event.healerId)} restaura ${name(event.targetId)}`;
          return event.cured?.length
            ? `${healed}, que ${curedPhrase(event.cured)}.`
            : `${healed}.`;
        }
        case 'usou_item': {
          audio?.play('cura');
          const catalog = loadedRef.current;
          const itemName = (event.itemId !== undefined
            ? catalog?.items?.find((i) => i.id === event.itemId)?.name
            : catalog?.weapons.find((w) => w.id === event.weaponId)?.name) ?? 'um item';
          const effects: string[] = [];
          if (event.amount > 0) effects.push(`recupera ${event.amount} PV`);
          if (event.cured.length > 0) effects.push(curedPhrase(event.cured));
          const outcome = effects.join(' e ') || 'nada muda';
          return event.targetId === event.unitId
            ? `${name(event.unitId)} usa ${itemName} e ${outcome}.`
            : `${name(event.unitId)} usa ${itemName} em ${name(event.targetId)}, que ${outcome}.`;
        }
        case 'condicao_aplicada':
          return `${CONDITION_MARK[event.condition]} ${name(event.unitId)} fica ${
            conditionLabel(event.condition).toLowerCase()
          }${event.turns ? ` (${event.turns} ${event.turns === 1 ? 'turno' : 'turnos'})` : ''}.`;
        case 'condicao_efeito':
          return `${CONDITION_MARK[event.condition]} ${name(event.unitId)} sofre ${event.amount} ${
            event.condition === 'queimando' ? 'da queimadura' : 'do veneno'
          }.`;
        case 'condicao_removida':
          if (event.reason === 'acordou') return `${name(event.unitId)} acorda com o golpe.`;
          // A cura já foi contada pelo uso do item ou do cajado.
          if (event.reason === 'curada') return null;
          return `${name(event.unitId)} não está mais ${conditionLabel(event.condition).toLowerCase()}.`;
        case 'trocou_itens': {
          audio?.play('confirma');
          const partes: string[] = [];
          if (event.given > 0) partes.push(`entrega ${event.given}`);
          if (event.taken > 0) partes.push(`recebe ${event.taken}`);
          return `🎒 ${name(event.unitId)} troca itens com ${name(event.targetId)}${
            partes.length ? ` — ${partes.join(' e ')}` : ''
          }.`;
        }
        case 'recrutou':
          audio?.play('confirma');
          return `💬 ${name(event.unitId)} conversa com ${name(event.targetId)}, que entra para o esquadrão.`;
        case 'morreu':
          audio?.play('morte');
          return `✝ ${name(event.unitId)} cai em combate.`;
        case 'ganhou_exp':
          return `✨ ${name(event.unitId)} ganha ${event.amount} EXP (${event.exp}/100).`;
        case 'subiu_nivel': {
          // Sem som aqui: quem toca a fanfarra é `onLevelUpAnimation`, no
          // instante em que o quadro de nível aparece. Este gancho só corre
          // DEPOIS que o jogador o dispensa, e tocar de novo soava duas vezes.
          const gains = Object.entries(event.gains).map(([k, v]) => `${k} +${v}`).join(', ');
          return `↑ ${name(event.unitId)} alcança o nível ${event.level} (${gains}).`;
        }
        case 'arma_quebrou':
          return `A arma de ${name(event.unitId)} se parte.`;
        case 'abriu_bau': {
          audio?.play('confirma');
          // O prêmio vem nas duas formas da bolsa: arma do acervo ou item do
          // RPG. Procurar só entre as armas deixava toda poção de baú sem nome.
          const prize = event.itemId !== undefined
            ? loadedRef.current?.items?.find((i) => i.id === event.itemId)
            : loadedRef.current?.weapons.find((w) => w.id === event.weaponId);
          return `🧰 ${name(event.unitId)} abre o baú${
            event.usedKey ? ' com uma chave' : ''
          } e encontra ${prize?.name ?? 'um item'}.`;
        }
        default:
          return null;
      }
    },
    [],
  );

  const toggleFullscreenRef = useRef<() => void>(() => undefined);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [optionsCursor, setOptionsCursor] = useState<number>(0);
  interface LevelUpData {
    unitId: number;
    name: string;
    className: string;
    portrait?: { sheet: string; index: number } | undefined;
    battler?: string | undefined;
    battlerAsPortrait?: boolean | undefined;
    oldLevel: number;
    newLevel: number;
    oldStats: Stats;
    newStats: Stats;
    gains: Partial<Stats>;
    resolve?: (() => void) | undefined;
  }

  const [levelUpBanner, setLevelUpBanner] = useState<LevelUpData | null>(null);

  const dismissLevelUp = useCallback(() => {
    setLevelUpBanner((curr) => {
      if (curr?.resolve) {
        try {
          curr.resolve();
        } catch {
          // ignora falha em resolve
        }
      }
      return null;
    });
  }, []);

  const [bannerActive, setBannerActive] = useState(true);
  const bannerActiveRef = useRef(bannerActive);
  bannerActiveRef.current = bannerActive;

  /*
   * A conversa que o capítulo escreveu para cada recruta.
   *
   * Vive na tela, e não no motor: o motor decide QUE a unidade muda de lado, e
   * o texto do que foi dito é assunto de quem desenha. A ligação é o nome com
   * que o mapa plantou a unidade — o mesmo que o autor escreve no capítulo.
   */
  const recruitLines = useMemo(() => {
    const book = new Map<number, string>();
    const list = chapter?.recruits ?? [];
    if (list.length === 0 || !skirmish) return book;
    for (const unit of skirmish.state.units) {
      const entry = list.find(
        (r) => r.unitName.trim().toLowerCase() === unit.name.trim().toLowerCase(),
      );
      if (entry?.dialogue?.trim()) book.set(unit.id, entry.dialogue.trim());
    }
    return book;
  }, [chapter, skirmish]);
  const recruitLinesRef = useRef(recruitLines);
  recruitLinesRef.current = recruitLines;

  /** A fala em cartaz, quando alguém acabou de mudar de lado. */
  const [recruitScene, setRecruitScene] = useState<{ name: string; text: string } | null>(null);
  /**
   * Quem mudou de lado NESTA batalha, pelo personagem do acervo.
   *
   * O servidor confere a lista contra o que o capítulo permite antes de
   * gravar; ela viaja junto com o desfecho para que o recruta entre no
   * esquadrão do jogador de verdade, e não só até o fim da partida.
   */
  const recruitedRef = useRef<Set<number>>(new Set());

  const game = useSkirmish(
    skirmish?.state ?? EMPTY_STATE,
    loaded?.content ?? EMPTY_CONTENT,
    {
      onLevelUpAnimation: (event, before, after) => {
        return new Promise<void>((resolve) => {
          const unitBefore = unitById(before, event.unitId);
          const unitAfter = unitById(after, event.unitId);
          const p = presentation.get(event.unitId);
          const klass = loaded?.classes.find((k) => k.id === (unitAfter?.classId ?? unitBefore?.classId));
          const fallbackStats: Stats = unitAfter?.stats ?? unitBefore?.stats ?? {
            hp: 18, str: 5, mag: 1, skl: 7, spd: 8, lck: 4, def: 4, res: 1,
          };
          setLevelUpBanner({
            unitId: event.unitId,
            name: unitAfter?.name ?? unitBefore?.name ?? `#${event.unitId}`,
            className: klass?.name ?? 'Combatente',
            portrait: p?.portrait,
            battler: p?.battler,
            battlerAsPortrait: p?.battlerAsPortrait,
            oldLevel: unitBefore?.level ?? Math.max(1, event.level - 1),
            newLevel: event.level,
            oldStats: unitBefore?.stats ?? fallbackStats,
            newStats: unitAfter?.stats ?? fallbackStats,
            gains: event.gains,
            resolve,
          });
          audioRef.current?.play('nivel');
        });
      },
      onEvents: (events, before, after) => {
        const lines = events.map((e) => describe(before, e)).filter((l): l is string => Boolean(l));
        if (lines.length) setLog((l) => [...lines, ...l].slice(0, 40));

        /*
         * Alguém mudou de lado: a fala entra em cena e o recruta é anotado.
         *
         * A anotação é pelo personagem do acervo (`characterId`), e não pelo
         * id da unidade: o id só existe nesta partida, e quem precisa dele
         * depois é o esquadrão guardado do jogador.
         */
        const joined = events.find((e): e is Extract<Event, { type: 'recrutou' }> => e.type === 'recrutou');
        if (joined) {
          const who = unitById(after, joined.targetId) ?? unitById(before, joined.targetId);
          const characterId = presentation.get(joined.targetId)?.characterId;
          if (typeof characterId === 'number') recruitedRef.current.add(characterId);
          const text = recruitLinesRef.current.get(joined.targetId);
          if (who && text) setRecruitScene({ name: who.name, text });
        }

        /*
         * A subida de nível NÃO abre a tela aqui.
         *
         * Quem a abre é `onLevelUpAnimation`, que roda antes deste gancho e
         * segura a jogada até o jogador dispensar o quadro. Abrir de novo aqui
         * mostrava o mesmo nível duas vezes: uma na animação e outra assim que
         * ela terminava. A linha do diário continua saindo de `describe`.
         */
      },
      onOpenOptions: () => {
        setOptionsOpen(true);
      },
      onCycleZoom: (step) => setZoom((z) => (step > 0 ? cycleZoom(z) : stepZoom(z, -1))),
      onToggleSound: () => {
        audioRef.current?.unlock();
        setMuted((m) => !m);
      },
      onToggleFullscreen: () => toggleFullscreenRef.current(),
      onInvalid: (message) => {
        audioRef.current?.play('erro');
        setNotice(message);
      },
      onCursor: () => audioRef.current?.play('cursor'),
      onConfirm: () => audioRef.current?.play('confirma'),
      onCancel: () => audioRef.current?.play('cancela'),
      onPreviewMove: (unitId, path) => {
        void boardRef.current?.previewMove(unitId, path);
      },
      onCancelPreview: () => boardRef.current?.cancelPreview(),
      onCommitPreview: () => boardRef.current?.commitPreview(),
      onMoveAnimation: async (unitId, path) => {
        const board = boardRef.current;
        if (!board) return;
        // A prévia já levou a unidade até o destino quando o jogador marcou a
        // casa; reanimar aqui a faria voltar ao ponto de partida e refazer o
        // caminho, com o passo aparecendo duas vezes.
        if (board.hasPreview(unitId)) {
          board.commitPreview();
          return;
        }
        await board.animateMove(unitId, path);
      },
      onCombatAnimation: (event, before) => {
        return new Promise<void>((resolve) => {
          setActiveCombat({ event, before, resolve });
        });
      },
    },
    { difficulty, setDifficulty: changeDifficulty, bannerActive },
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__oz_skirmish = {
        game,
        setActiveCombat,
        audio: audioRef.current,
        loaded,
        board: boardRef.current,
      };
    }
  });

  const over = game.state.status !== 'em_andamento';

  // --- partidas gravadas ----------------------------------------------------

  /** Quantas gravações existem, para a lista dentro da partida saber o que mostrar. */
  const [saveSlots, setSaveSlots] = useState(() => listSaves());
  const refreshSaves = useCallback(() => setSaveSlots(listSaves()), []);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);

  /**
   * A partida de agora, no formato que sobrevive a fechar a aba.
   *
   * `null` quando não há o que guardar: a partida acabou, ou é um desafio
   * online — lá o adversário é outra pessoa esperando do outro lado, e
   * retomar sozinho no dia seguinte não faria sentido nenhum.
   */
  const snapshot = useCallback((): SaveGame | null => {
    if (over || matchMode === 'online' || !loaded) return null;
    const living = livingUnits(game.state);
    return {
      version: 1,
      savedAt: new Date().toISOString(),
      mode: chapter ? 'campanha' : 'escaramuca',
      mapId: loaded.map.id,
      mapName: loaded.map.name || `Mapa #${loaded.map.id}`,
      seed,
      difficulty,
      turn: game.state.turn,
      allies: living.filter((u) => u.team === 'jogador').length,
      enemies: living.filter((u) => u.team === 'inimigo').length,
      state: serialize(game.state),
      presentation: [...presentation.entries()],
      ...(chapter
        ? {
            chapter: {
              id: chapter.id,
              title: chapter.title,
              campaignSlug: chapter.campaignSlug ?? '',
              ordinal: chapter.ordinal,
            },
          }
        : {}),
    };
  }, [over, matchMode, loaded, game.state, chapter, seed, difficulty, presentation]);

  const storeSave = useCallback(
    (slot: SlotId): void => {
      const data = snapshot();
      if (!data) {
        setNotice('Não há partida em curso para guardar.');
        return;
      }
      if (writeSave(slot, data)) {
        refreshSaves();
        setNotice(
          slot === AUTO_SLOT
            ? 'Partida guardada automaticamente.'
            : `Partida guardada no espaço ${slot}.`,
        );
      } else {
        setNotice('O navegador recusou a gravação — pode estar sem espaço.');
      }
    },
    [snapshot, refreshSaves],
  );

  /*
   * A gravação automática, uma por turno do jogador.
   *
   * No começo da SUA fase, e não a cada jogada: guardar no meio do turno
   * devolveria o jogador a um tabuleiro com metade do esquadrão já gasto, sem
   * que ele soubesse quem era quem. E não durante a fase da máquina, que é um
   * estado transitório de onde não se retoma nada.
   */
  const autoSavedAt = useRef<string>('');
  useEffect(() => {
    if (over || screen !== 'batalha') return;
    if (game.state.phase !== 'jogador' || game.isCpuActing) return;
    // O primeiro turno não vale gravação: nada aconteceu ainda, e retomá-lo é
    // o mesmo que começar a partida — com o efeito ruim de a lista de
    // capítulos passar a oferecer "Continuar (turno 1)" para sempre.
    if (game.state.turn <= 1) return;
    const stamp = `${mapId}:${seed}:${game.state.turn}`;
    if (autoSavedAt.current === stamp) return;
    autoSavedAt.current = stamp;
    const data = snapshot();
    if (data) {
      writeSave(AUTO_SLOT, data);
      refreshSaves();
    }
    // `snapshot` muda a cada estado; entrar aqui só pela virada de turno é o
    // que mantém a gravação em uma por turno.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.state.turn, game.state.phase, game.isCpuActing, over, screen, mapId, seed]);

  /**
   * Retoma uma gravação: traz o mapa dela, e o capítulo dela quando é de campanha.
   *
   * O capítulo é RELIDO do servidor em vez de sair da gravação: recompensa,
   * objetivo e elenco permitido podem ter mudado desde o dia em que a partida
   * foi guardada, e é a definição de hoje que vale.
   */
  const resumeSave = useCallback(
    (save: SaveGame): void => {
      setActiveDraft(undefined);
      setDifficulty(save.difficulty);
      setSeed(save.seed);
      setRestoring(save);
      setMapId(save.mapId);
      setLog([]);
      setScreen('batalha');

      if (save.mode !== 'campanha' || !save.chapter?.campaignSlug) {
        setChapter(null);
        return;
      }

      const wanted = save.chapter;
      fetch(`/api/historia/campanhas/${encodeURIComponent(wanted.campaignSlug)}`, {
        credentials: 'include',
      })
        .then(async (r) => (r.ok ? ((await r.json()) as { chapters: Chapter[] }) : null))
        .then((data) => {
          const found = data?.chapters.find((c) => c.id === wanted.id);
          setChapter(found ? { ...found, campaignSlug: wanted.campaignSlug } : null);
        })
        .catch(() => setChapter(null));
    },
    [],
  );

  // --- o capítulo, do começo ao fim ----------------------------------------

  /**
   * As cenas em cartaz — a abertura do capítulo, ou o que se diz ao fim dele.
   *
   * Enquanto elas passam, o teclado é delas: sem isto, o Espaço que avança a
   * fala também moveria o cursor do tabuleiro por baixo da cena.
   */
  const [cutscene, setCutscene] = useState<readonly Scene[] | null>(null);
  /** O capítulo seguinte, quando este foi vencido e existe um. */
  const [nextChapter, setNextChapter] = useState<Chapter | null>(null);

  const modalBlocksKeysRef = useRef(false);
  modalBlocksKeysRef.current = cutscene !== null || recruitScene !== null || saveMenuOpen;

  /*
   * De qual capítulo a abertura já passou.
   *
   * A abertura passava na LISTA de capítulos, antes de a batalha existir: a
   * conversa que antecede o combate acontecia sobre a lista, com o fundo da
   * cena tapando o mapa de que ela fala. Agora ela passa aqui, quando o
   * tabuleiro já está montado — e esta referência é o que a faz passar uma vez
   * só, e não a cada remontagem da partida.
   */
  const openingShownFor = useRef<number | null>(null);

  /** Recomeça a tela inteira num capítulo — o seguinte, ou este de novo. */
  const startChapter = useCallback(
    (next: Chapter, slug: string) => {
      /*
       * O tabuleiro anterior sai de cena antes de o próximo entrar.
       *
       * Sem isto, os sprites do capítulo vencido sobreviviam à troca e o mapa
       * novo abria com os mortos do anterior por cima — e uma animação de
       * combate ainda em curso ficaria pendurada esperando um estado que já
       * não existe. É a mesma limpeza de `handleRestart`.
       */
      if (activeCombat) {
        activeCombat.resolve();
        setActiveCombat(null);
      }
      boardRef.current?.clearUnits();
      setRestoring(null);
      setActiveDraft(undefined);
      setNextChapter(null);
      setChapter({ ...next, campaignSlug: next.campaignSlug ?? slug });
      setDifficulty(next.difficulty ?? 'normal');
      setMapId(next.mapId);
      setSeed(Math.floor(Math.random() * 0x7fffffff));
      setLog([]);
      setScreen('batalha');
      setCutscene(null);
      // Rejogar o mesmo capítulo é começá-lo de novo, abertura inclusive.
      openingShownFor.current = null;
    },
    [activeCombat],
  );

  /*
   * A abertura do capítulo, por cima do mapa em que ela acontece.
   *
   * Espera o tabuleiro existir (`skirmish`): mostrar a conversa antes do mapa
   * carregar devolveria a cena a um fundo vazio, que é o que ela tinha antes.
   * Quem está retomando uma gravação não a vê — já a viu no turno um.
   */
  useEffect(() => {
    if (!chapter || !skirmish || restoring) return;
    if (openingShownFor.current === chapter.id) return;
    openingShownFor.current = chapter.id;
    const opening = chapter.scenes.filter((sc) => sc.moment === 'abertura');
    if (opening.length > 0) setCutscene(opening);
  }, [chapter, skirmish, restoring]);

  // A partida nova não herda os recrutas da anterior.
  useEffect(() => {
    recruitedRef.current = new Set();
    setRecruitScene(null);
  }, [skirmish]);

  useEffect(() => {
    if (over) {
      setBannerActive(false);
      bannerActiveRef.current = false;
      boardRef.current?.setBannerActive(false);
      return;
    }
    setBannerActive(true);
    bannerActiveRef.current = true;
    boardRef.current?.setBannerActive(true);
  }, [game.state.phase, game.state.turn, over]);

  const { recordMatch } = useAuth();
  const recordedRef = useRef(false);

  const [verdictSelection, setVerdictSelection] = useState<'restart' | 'change_map'>('restart');
  /** Quantos Emblemas esta vitória rendeu, quando rendeu. */
  const [reward, setReward] = useState<number | null>(null);
  const [unlockedRecruits, setUnlockedRecruits] = useState<Array<{ characterId: number; name: string; level: number }>>([]);
  const [grantedItems, setGrantedItems] = useState<Array<{ type: string; id: number; quantity: number; name?: string }>>([]);
  const [selectedNextMapId, setSelectedNextMapId] = useState<number>(mapId);

  useEffect(() => {
    if (mapList.length > 0) {
      const currentIdx = mapList.findIndex((m) => m.id === mapId);
      const nextIdx = (currentIdx + 1) % mapList.length;
      setSelectedNextMapId(mapList[nextIdx]?.id ?? mapId);
    }
  }, [mapId, mapList]);

  useEffect(() => {
    if (over) {
      setVerdictSelection('restart');
    }
  }, [over]);

  const handleRestart = useCallback(() => {
    if (activeCombat) {
      activeCombat.resolve();
      setActiveCombat(null);
    }
    boardRef.current?.clearUnits();
    // Recomeçar é recomeçar: a gravação que estava sendo retomada sai de cena,
    // senão `reset` devolveria a partida ao ponto em que ela foi guardada.
    setRestoring(null);
    reseed();
    game.reset();
    setLog([]);
  }, [activeCombat, game, reseed]);

  const handleChangeMap = useCallback((newMapId: number) => {
    if (activeCombat) {
      activeCombat.resolve();
      setActiveCombat(null);
    }
    boardRef.current?.clearUnits();
    setRestoring(null);
    setActiveDraft(undefined);
    setMapId(newMapId);
    setLog([]);
  }, [activeCombat]);

  const cycleNextMap = useCallback((delta: number) => {
    if (mapList.length <= 1) return;
    setSelectedNextMapId((current) => {
      const idx = mapList.findIndex((m) => m.id === current);
      const validIdx = idx >= 0 ? idx : 0;
      const nextIdx = (validIdx + delta + mapList.length) % mapList.length;
      return mapList[nextIdx]?.id ?? current;
    });
  }, [mapList]);

  /*
   * As saídas da tela de fim de CAPÍTULO.
   *
   * A escaramuça tem duas ("jogar de novo", "trocar de mapa") e o capítulo tem
   * outras três — e as do capítulo não existiam para o teclado nem para o
   * controle. Confirmar na tela de vitória caía sempre em `handleRestart`, que
   * é "recomeçar o mapa atual": o jogador via "Próximo capítulo" escrito na
   * tela, apertava A, e a campanha voltava ao capítulo que ele acabara de
   * vencer. Agora a lista é a mesma para o mouse, o teclado e o controle.
   */
  const chapterVerdictOptions = useMemo<readonly ('proximo' | 'rejogar' | 'campanha')[]>(() => {
    if (!chapter) return [];
    return [
      ...(game.state.status === 'vitoria' && nextChapter ? (['proximo'] as const) : []),
      'rejogar' as const,
      'campanha' as const,
    ];
  }, [chapter, nextChapter, game.state.status]);
  const [chapterChoice, setChapterChoice] = useState(0);
  // A lista muda quando o próximo capítulo chega do servidor, depois de a tela
  // já estar aberta: sem isto o cursor ficaria apontando para a linha errada.
  useEffect(() => {
    setChapterChoice(0);
  }, [chapterVerdictOptions]);

  const runChapterChoice = useCallback(
    (option: 'proximo' | 'rejogar' | 'campanha') => {
      audioRef.current?.unlock();
      if (option === 'proximo') {
        if (!nextChapter) return;
        audioRef.current?.play('confirma');
        startChapter(nextChapter, chapter?.campaignSlug ?? '');
        return;
      }
      if (option === 'rejogar') {
        audioRef.current?.play('confirma');
        handleRestart();
        return;
      }
      audioRef.current?.play('cancela');
      onExitToCampaign?.();
    },
    [nextChapter, chapter, startChapter, handleRestart, onExitToCampaign],
  );

  const optionsList = useMemo(() => {
    const list: string[] = ['mode'];
    if (game.playMode === 'vs_cpu') {
      list.push('diff', 'speed');
    }
    list.push('map', 'save', 'restart', 'close');
    return list;
  }, [game.playMode]);

  const cycleMap = useCallback((delta: number) => {
    if (mapList.length <= 1) return;
    const currentIdx = mapList.findIndex((m) => m.id === mapId);
    const validIdx = currentIdx >= 0 ? currentIdx : 0;
    const nextIdx = (validIdx + delta + mapList.length) % mapList.length;
    const nextMap = mapList[nextIdx];
    if (nextMap) {
      if (activeCombat) {
        activeCombat.resolve();
        setActiveCombat(null);
      }
      setRestoring(null);
      setActiveDraft(undefined);
      setMapId(nextMap.id);
      setLog([]);
    }
  }, [mapList, mapId, activeCombat]);

  const cycleOptionValue = useCallback((delta: number) => {
    const currentOpt = optionsList[optionsCursor] ?? 'mode';
    audioRef.current?.play('cursor');
    if (currentOpt === 'mode') {
      game.setPlayMode(game.playMode === 'vs_cpu' ? 'hotseat' : 'vs_cpu');
    } else if (currentOpt === 'diff') {
      const diffs = ['facil', 'normal', 'dificil'] as const;
      const idx = diffs.indexOf(game.difficulty);
      const nextDiff = diffs[(idx + delta + diffs.length) % diffs.length]!;
      game.setDifficulty(nextDiff);
    } else if (currentOpt === 'speed') {
      game.setCpuSpeed(game.cpuSpeed === 'normal' ? 'rapido' : 'normal');
    } else if (currentOpt === 'map') {
      cycleMap(delta);
    }
  }, [optionsList, optionsCursor, game, cycleMap]);

  useEffect(() => {
    if (over && !recordedRef.current) {
      recordedRef.current = true;
      const won = game.state.status === 'vitoria';
      void recordMatch(won ? 'vitoria' : 'derrota');

      /*
       * A partida acabou: a gravação automática DELA some.
       *
       * Os espaços que o jogador escolheu ficam — são dele. Mas a automática é
       * do jogo, e deixá-la para trás faria a lista de capítulos oferecer
       * "Continuar" para uma batalha que já terminou.
       */
      const auto = readSave(AUTO_SLOT);
      if (auto && auto.seed === game.state.seed) {
        deleteSave(AUTO_SLOT);
        refreshSaves();
      }

      if (won) {
        audioRef.current?.play('vitoria');
      } else if (game.state.status === 'derrota') {
        audioRef.current?.play('morte');
      }

      /*
       * O desfecho volta a quem mandou a batalha.
       *
       * Quanto isso vale em Emblemas e se o próximo capítulo abre são decisões
       * do servidor — esta tela só conta o que aconteceu. Uma falha aqui não
       * pode estragar a tela de vitória: o recado se perde, o jogador vê o
       * resultado, e a recompensa continua paga na próxima vez que ele vencer.
       */
      if (chapter) {
        /*
         * O capítulo acabou: o que o esquadrão ganhou fica guardado.
         *
         * Só a campanha grava — e só ela porque só ela é a progressão do
         * jogador. O desafio online lê o mesmo registro e não escreve nada
         * (ver `MatchState.progression`), e a escaramuça nem sequer o lê.
         *
         * Grava-se o esquadrão inteiro, inclusive quem caiu em combate: perder
         * uma unidade não pode apagar os níveis que ela já tinha. O servidor
         * confere cada ficha antes de aceitar, e nunca deixa nada regredir.
         */
        rosterWriteRef.current = saveRoster(
          game.state.units
            .filter((u) => u.team === 'jogador')
            .map((u) => ({ unit: u, characterId: presentation.get(u.id)?.characterId }))
            .filter((row): row is { unit: typeof row.unit; characterId: number } =>
              typeof row.characterId === 'number',
            )
            /*
             * Quem mudou de lado só fica se o capítulo foi VENCIDO.
             *
             * Sem este corte, bastava conversar com o recruta e perder de
             * propósito para ficar com ele: a batalha é o preço do recruta, e
             * quem a perde tenta de novo.
             */
            .filter((row) => won || !recruitedRef.current.has(row.characterId))
            .map(({ unit, characterId }) => ({
              characterId,
              level: unit.level,
              exp: unit.exp,
              stats: { ...unit.stats },
              // Cada espaço com a sua forma: arma por `weaponId`, item do RPG
              // por `itemId`. Gravar tudo como arma transformaria o Antídoto
              // (item 13) na arma 13 no capítulo seguinte.
              inventory: unit.inventory.map((i): ItemInstance =>
                i.weaponId !== undefined
                  ? { weaponId: i.weaponId, usesLeft: i.usesLeft }
                  : { itemId: i.itemId, usesLeft: i.usesLeft },
              ),
            })),
        );

        void fetch('/api/historia/capitulo/concluir', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chapterId: chapter.id,
            won,
            turns: game.state.turn,
            // Quem foi convencido em campo. O servidor confere contra o que o
            // capítulo permite antes de pôr qualquer um no esquadrão.
            recruited: [...recruitedRef.current],
          }),
        })
          .then(async (response) => {
            if (!response.ok) return;
            const payload = (await response.json()) as {
              credited?: number;
              unlockedCharacters?: Array<{ characterId: number; name: string; level: number }>;
              grantedItems?: Array<{ type: string; id: number; quantity: number; name?: string }>;
            };
            if (payload.credited && payload.credited > 0) {
              setReward(payload.credited);
            }
            if (payload.unlockedCharacters && payload.unlockedCharacters.length > 0) {
              setUnlockedRecruits(payload.unlockedCharacters);
            }
            if (payload.grantedItems && payload.grantedItems.length > 0) {
              setGrantedItems(payload.grantedItems);
            }
          })
          .catch(() => undefined)
          /*
           * Só DEPOIS de o servidor registrar a vitória é que se pergunta qual
           * é o próximo capítulo.
           *
           * A ordem importa: é o registro que destranca o seguinte, e
           * perguntar antes traria o capítulo ainda fechado — com o botão
           * "Próximo capítulo" apagado logo depois de o jogador tê-lo aberto.
           */
          .then(() => {
            if (!won || !chapter.campaignSlug) return;
            return fetch(`/api/historia/campanhas/${encodeURIComponent(chapter.campaignSlug)}`, {
              credentials: 'include',
            })
              .then(async (r) => (r.ok ? ((await r.json()) as { chapters: Chapter[] }) : null))
              .then((data) => {
                if (!data) return;
                const slug = chapter.campaignSlug ?? '';
                const following = data.chapters
                  .filter((c) => c.ordinal > chapter.ordinal)
                  .sort((a, b) => a.ordinal - b.ordinal)[0];
                setNextChapter(following ? { ...following, campaignSlug: slug } : null);
              })
              .catch(() => undefined);
          })
          .catch(() => undefined);
      } else if (onlineLobby) {
        // O vencedor é contado a partir de quem ESTÁ jogando: `won` é a vitória
        // deste navegador, seja ele do anfitrião ou do convidado.
        const winnerId = onlineWinner(onlineLobby, won);
        const closing = winnerId === null
          ? Promise.resolve()
          : fetch('/api/online/salas/encerrar', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ lobbyId: onlineLobby.id, winnerId }),
            }).then(() => undefined).catch(() => undefined);

        /*
         * O desafio online paga em Emblemas, e só em Emblemas.
         *
         * Nada de nível e nada de item: os dois lados levam os personagens
         * como a campanha os deixou, e vencer não pode mexer na ficha de
         * ninguém — quem ganhasse nível contra outro jogador estaria sendo
         * premiado duas vezes, e quem perdesse sairia do desafio com o
         * esquadrão pior do que entrou. O que se ganha são Emblemas, que se
         * trocam por itens na Loja depois.
         *
         * A referência carrega a sala: o servidor recusa o pagamento repetido
         * da mesma, então recarregar a tela de vitória não paga duas vezes. E
         * só paga a sala encerrada com este jogador como vencedor — daí pedir
         * a recompensa depois do encerramento, e não ao mesmo tempo.
         */
        if (won) {
          void closing.then(() => fetch('/api/economia/recompensa', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kind: 'online', reference: `sala:${onlineLobby.id}` }),
          }))
            .then(async (response) => {
              if (!response.ok) return;
              const payload = (await response.json()) as { credited?: number };
              if (payload.credited && payload.credited > 0) setReward(payload.credited);
            })
            .catch(() => undefined);
        }
      } else if (won) {
        void fetch('/api/economia/recompensa', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind: 'escaramuca', reference: `mapa:${mapId}:${game.state.seed}` }),
        })
          .then(async (response) => {
            if (!response.ok) return;
            const payload = (await response.json()) as { credited?: number };
            if (payload.credited && payload.credited > 0) setReward(payload.credited);
          })
          .catch(() => undefined);
      }
      /*
       * O capítulo fecha com o que ele tem a dizer.
       *
       * As cenas de vitória e de derrota já estavam gravadas e nunca eram
       * mostradas: a batalha terminava na mesma tela genérica da escaramuça, e
       * o jogador nunca via o desfecho que a campanha escreveu.
       */
      if (chapter) {
        const closing = chapter.scenes.filter(
          (sc) => sc.moment === (won ? 'vitoria' : 'derrota'),
        );
        if (closing.length > 0) setCutscene(closing);
      }
    } else if (!over) {
      recordedRef.current = false;
      setReward(null);
      setUnlockedRecruits([]);
      setGrantedItems([]);
      setNextChapter(null);
    }
  }, [over, game.state.status, recordMatch, chapter, onlineLobby, mapId]);

  // --- joystick / gamepad ---------------------------------------------------

  const gamepad = useGamepad(
    {
      onDirection: (dx, dy) => {
        if (levelUpBanner) return;
        if (bannerActiveRef.current) return;
        if (activeCombat) return;
        if (optionsOpen) {
          if (dy > 0) {
            setOptionsCursor((c) => (c + 1) % optionsList.length);
            audioRef.current?.play('cursor');
          } else if (dy < 0) {
            setOptionsCursor((c) => (c - 1 + optionsList.length) % optionsList.length);
            audioRef.current?.play('cursor');
          } else if (dx !== 0) {
            cycleOptionValue(dx > 0 ? 1 : -1);
          }
          return;
        }
        if (over) {
          // No capítulo, o D-Pad percorre as saídas do capítulo. Ver
          // `chapterVerdictOptions`.
          if (chapterVerdictOptions.length > 0) {
            const size = chapterVerdictOptions.length;
            if (dy !== 0) {
              setChapterChoice((c) => (c + (dy > 0 ? 1 : -1) + size) % size);
              audioRef.current?.play('cursor');
            }
            return;
          }
          if (dy > 0) {
            setVerdictSelection('change_map');
            audioRef.current?.play('cursor');
          } else if (dy < 0) {
            setVerdictSelection('restart');
            audioRef.current?.play('cursor');
          } else if (dx !== 0 && verdictSelection === 'change_map') {
            cycleNextMap(dx > 0 ? 1 : -1);
            audioRef.current?.play('cursor');
          }
          return;
        }
        // O menu de campo atravessa: é por ele que se desliga a automática.
        if ((game.isCpuTurn || game.isCpuActing) && game.selection.mode !== 'campo') return;
        game.moveCursor({ x: dx, y: dy });
      },
      onConfirm: () => {
        if (levelUpBanner) {
          audioRef.current?.play('confirma');
          dismissLevelUp();
          return;
        }
        if (bannerActiveRef.current) return;
        if (activeCombat) return;
        audioRef.current?.unlock();
        if (optionsOpen) {
          const currentOpt = optionsList[optionsCursor];
          if (currentOpt === 'save') {
            audioRef.current?.play('confirma');
            setSaveMenuOpen(true);
          } else if (currentOpt === 'restart') {
            audioRef.current?.play('confirma');
            handleRestart();
            setOptionsOpen(false);
          } else if (currentOpt === 'close') {
            audioRef.current?.play('cancela');
            setOptionsOpen(false);
          } else {
            cycleOptionValue(1);
          }
          return;
        }
        if (over) {
          if (chapterVerdictOptions.length > 0) {
            const option = chapterVerdictOptions[chapterChoice % chapterVerdictOptions.length];
            if (option) runChapterChoice(option);
            return;
          }
          audioRef.current?.play('confirma');
          if (verdictSelection === 'restart') {
            handleRestart();
          } else {
            handleChangeMap(selectedNextMapId);
          }
          return;
        }
        if ((game.isCpuTurn || game.isCpuActing) && game.selection.mode !== 'campo') return;
        game.confirm();
      },
      onCancel: () => {
        if (levelUpBanner) {
          audioRef.current?.play('cancela');
          dismissLevelUp();
          return;
        }
        if (bannerActiveRef.current) return;
        if (activeCombat) return;
        // Pressionar Cancelar (B) no joystick desliga imediatamente a auto-batalha
        if (game.autoBattle) {
          audioRef.current?.play('cancela');
          game.setAutoBattle(false);
          return;
        }
        if (optionsOpen) {
          audioRef.current?.play('cancela');
          setOptionsOpen(false);
          return;
        }
        if (over) return;
        if (game.isCpuTurn || game.isCpuActing) {
          if (game.selection.mode === 'campo') game.cancel();
          else game.openFieldMenu();
          return;
        }
        if (game.selection.mode === 'trocando') {
          game.finishTrade();
          return;
        }
        game.cancel();
      },
      onCycleTarget: (step) => {
        if (bannerActiveRef.current) return;
        if (activeCombat || levelUpBanner || optionsOpen) return;
        if (over) {
          if (verdictSelection === 'change_map') {
            cycleNextMap(step > 0 ? 1 : -1);
            audioRef.current?.play('cursor');
          }
          return;
        }
        if (game.isCpuTurn || game.isCpuActing) return;
        game.cycleTarget(step);
      },
      onCycleAlly: (direction) => {
        if (bannerActiveRef.current) return;
        if (activeCombat || levelUpBanner || optionsOpen) return;
        if (over) {
          if (verdictSelection === 'change_map') {
            cycleNextMap(direction);
            audioRef.current?.play('cursor');
          }
          return;
        }
        if (game.isCpuTurn || game.isCpuActing) return;
        game.cycleAlly(direction);
      },
      /*
       * Start abre as ORDENS DE CAMPO, desliga a auto-batalha ou fecha o level-up.
       */
      onEndPhase: () => {
        if (levelUpBanner) {
          audioRef.current?.play('confirma');
          dismissLevelUp();
          return;
        }
        if (bannerActiveRef.current) return;
        if (activeCombat) return;
        if (game.autoBattle) {
          audioRef.current?.play('cancela');
          game.setAutoBattle(false);
          return;
        }
        if (optionsOpen) {
          setOptionsOpen(false);
          return;
        }
        if (over) return;
        if (game.selection.mode === 'campo') game.cancel();
        else game.openFieldMenu();
      },
      onToggleMute: () => {
        setMuted((m) => !m);
      },
      onToggleThreat: () => {
        if (bannerActiveRef.current) return;
        if (activeCombat || levelUpBanner || optionsOpen || over) return;
        game.toggleAllThreat();
      },
      onZoomStep: (direction) => {
        if (activeCombat || levelUpBanner) return;
        setZoom((z) => stepZoom(z, direction));
      },
    },
    /*
     * O controle só comanda a partida enquanto a partida está na tela.
     *
     * A tela de abertura, a cena do capítulo e a lista de gravações têm o
     * próprio teclado; sem este desligamento o mesmo botão que confirma o
     * "Novo jogo" também confirmava uma jogada no tabuleiro por baixo, e a
     * partida começava com uma unidade já movida.
     */
    screen === 'batalha' &&
      !modalBlocksKeysRef.current &&
      (!activeCombat || Boolean(levelUpBanner)),
    confirmButton,
  );

  // --- tabuleiro ------------------------------------------------------------

  useEffect(() => {
    if (screen !== 'batalha') return;
    const host = hostRef.current;
    if (!host || !manifest || !loaded || !skirmish) return;

    let board: Board | null = new Board();
    let cancelled = false;
    let remount: number | undefined;

    board
      .init(host, manifest, loaded.map, loaded.tileset)
      .then(() => {
        if (cancelled || !board) return;
        board.onStep = () => {
          audioRef.current?.play('passo');
        };
        board.onTeleport = () => {
          audioRef.current?.play('teleporte');
        };
        // Um instante de folga antes de pedir outro contexto: logo depois de
        // a GPU reiniciar, o pedido pode falhar. O estado da partida não mora
        // no tabuleiro, então o novo sobe já com tudo no lugar.
        board.onContextLost = () => {
          window.clearTimeout(remount);
          remount = window.setTimeout(() => setBoardEpoch((n) => n + 1), 1000);
        };
        boardRef.current = board;
        board.setBannerActive(bannerActiveRef.current);
        setBoardReady((n) => n + 1);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));

    return () => {
      cancelled = true;
      window.clearTimeout(remount);
      boardRef.current = null;
      board?.destroy();
      board = null;
    };
  }, [manifest, loaded, Boolean(skirmish), screen, boardEpoch]);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    board.onStep = () => {
      audioRef.current?.play('passo');
    };
    board.onTeleport = () => {
      audioRef.current?.play('teleporte');
    };
    board.onTileClick = (at) => {
      if (game.isCpuTurn || game.isCpuActing || activeCombat || bannerActiveRef.current) return;
      audioRef.current?.unlock();
      if (game.selection.mode === 'livre' || game.selection.mode === 'movendo') {
        game.setCursor(at);
        game.confirm();
      } else if (game.selection.mode === 'mirando') {
        const clickedUnit = unitAt(game.state, at);
        if (clickedUnit && game.selection.targets.includes(clickedUnit.id)) {
          if (game.target?.id === clickedUnit.id) {
            game.confirm();
          } else {
            game.selectTarget(clickedUnit.id);
          }
        }
      }
    };
    board.onTileHover = (at) => {
      if (game.isCpuTurn || game.isCpuActing || activeCombat || bannerActiveRef.current) return;
      if (game.selection.mode === 'livre' || game.selection.mode === 'movendo') {
        game.setCursor(at);
      } else if (game.selection.mode === 'mirando') {
        const hoveredUnit = unitAt(game.state, at);
        if (hoveredUnit && game.selection.targets.includes(hoveredUnit.id)) {
          game.selectTarget(hoveredUnit.id);
        }
      }
    };
  }, [game, activeCombat, boardReady]);

  useEffect(() => {
    void boardRef.current?.syncChests(game.state);
    void boardRef.current?.syncUnits(game.state, presentation);
  }, [game.state, presentation, boardReady]);

  // O afastamento escolhido chega ao tabuleiro — inclusive na primeira vez, que
  // é por isso que `boardReady` está na lista: o Board sobe depois da tela.
  useEffect(() => {
    boardRef.current?.setZoom(zoomScale(zoom));
  }, [zoom, boardReady]);

  useEffect(() => {
    // O contorno acompanha o corpo de quem está debaixo do cursor: sobre um
    // monstro de mais de uma casa, ele abraça a área inteira.
    boardRef.current?.setCursor(game.cursor, game.hovered?.size);
  }, [game.cursor, game.hovered, boardReady]);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    board.clearOverlays();
    /*
     * A ordem é a de leitura, de baixo para cima.
     *
     * O alcance inimigo é o pano de fundo — fica aceso enquanto o jogador
     * levanta uma unidade, escolhe destino e mira. O que ele está fazendo
     * AGORA vai por cima; a marca de quais inimigos estão acesos, por último,
     * senão a mancha da própria unidade a esconderia justamente quando ele
     * quer desligá-los.
     */
    board.setOverlay('ameaca', game.threatTiles);
    // Mirar cura ou item pinta de verde: é ajuda, e vermelho diria "ataque".
    const helping = game.selection.mode === 'mirando' && game.selection.kind !== 'atacar';
    board.setOverlay(helping ? 'cura' : 'ataque', game.attackTiles);
    board.setOverlay('movimento', game.moveTiles);
    board.setThreatMarks(game.threatMarks);
    board.setPath(game.path);
  }, [
    game.moveTiles, game.attackTiles, game.threatTiles, game.threatMarks,
    game.path, game.selection, boardReady,
  ]);

  // --- teclado --------------------------------------------------------------

  /**
   * O ouvinte de teclado é montado UMA vez e lê o jogo por referência.
   *
   * Antes ele dependia de `game`, que muda de identidade a cada render: o
   * efeito refazia `remove`/`add` o tempo todo. Isso custava mais do que
   * desperdício. Pelo DOM, um ouvinte removido durante o disparo de um evento
   * deixa de ser chamado — e a primeira tecla da partida caía exatamente
   * nessa fresta, porque ela também destrava o áudio, o áudio muda estado, o
   * React reprocessa de imediato num evento discreto e o efeito se
   * reinscrevia no meio do próprio disparo. Resultado: a primeira seta depois
   * de abrir a página não fazia nada. Com o ouvinte estável não há fresta.
   */
  const keyEnvRef = useRef({
    game,
    activeCombat,
    over,
    verdictSelection,
    selectedNextMapId,
    chapterVerdictOptions,
    chapterChoice,
    setChapterChoice,
    runChapterChoice,
    handleRestart,
    handleChangeMap,
    cycleNextMap,
    optionsOpen,
    optionsCursor,
    optionsList,
    cycleOptionValue,
    levelUpBanner,
    dismissLevelUp,
    setOptionsOpen,
    setOptionsCursor,
    setLevelUpBanner,
  });
  keyEnvRef.current = {
    game,
    activeCombat,
    over,
    verdictSelection,
    selectedNextMapId,
    chapterVerdictOptions,
    chapterChoice,
    setChapterChoice,
    runChapterChoice,
    handleRestart,
    handleChangeMap,
    cycleNextMap,
    optionsOpen,
    optionsCursor,
    optionsList,
    cycleOptionValue,
    levelUpBanner,
    dismissLevelUp,
    setOptionsOpen,
    setOptionsCursor,
    setLevelUpBanner,
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const {
        game,
        activeCombat,
        over,
        verdictSelection,
        selectedNextMapId,
        chapterVerdictOptions,
        chapterChoice,
        setChapterChoice,
        runChapterChoice,
        handleRestart,
        handleChangeMap,
        cycleNextMap,
        optionsOpen,
        optionsCursor,
        optionsList,
        cycleOptionValue,
        levelUpBanner,
        dismissLevelUp,
        setOptionsOpen,
        setOptionsCursor,
        setLevelUpBanner,
      } = keyEnvRef.current;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      // Fora da batalha, o teclado é de quem está na tela — a abertura tem o
      // próprio menu, e ele responde às mesmas teclas.
      if (screenRef.current !== 'batalha') return;

      /*
       * Enquanto uma cena, uma conversa ou a lista de gravações está na
       * frente, o teclado é dela.
       *
       * Sem isto o mesmo Espaço que avança a fala também moveria o cursor do
       * tabuleiro por baixo, e o Esc que fecha a lista de gravações abriria o
       * menu de campo no mesmo gesto.
       */
      if (modalBlocksKeysRef.current) return;

      if (levelUpBanner) {
        if (
          event.key === 'Enter' ||
          event.key === ' ' ||
          event.key === 'z' ||
          event.key === 'x' ||
          event.key === 'Escape'
        ) {
          event.preventDefault();
          dismissLevelUp();
          return;
        }
      }

      if (activeCombat) return;
      if (bannerActiveRef.current) return;

      if (optionsOpen) {
        if (event.key === 'ArrowUp' || event.key === 'w') {
          event.preventDefault();
          audioRef.current?.play('cursor');
          setOptionsCursor((c) => (c - 1 + optionsList.length) % optionsList.length);
        } else if (event.key === 'ArrowDown' || event.key === 's') {
          event.preventDefault();
          audioRef.current?.play('cursor');
          setOptionsCursor((c) => (c + 1) % optionsList.length);
        } else if (event.key === 'ArrowLeft' || event.key === 'a') {
          event.preventDefault();
          cycleOptionValue(-1);
        } else if (event.key === 'ArrowRight' || event.key === 'd') {
          event.preventDefault();
          cycleOptionValue(1);
        } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'z') {
          event.preventDefault();
          const currentOpt = optionsList[optionsCursor];
          if (currentOpt === 'save') {
            audioRef.current?.play('confirma');
            setSaveMenuOpen(true);
          } else if (currentOpt === 'restart') {
            audioRef.current?.play('confirma');
            handleRestart();
            setOptionsOpen(false);
          } else if (currentOpt === 'close') {
            audioRef.current?.play('cancela');
            setOptionsOpen(false);
          } else {
            cycleOptionValue(1);
          }
        } else if (event.key === 'Escape' || event.key === 'x') {
          event.preventDefault();
          audioRef.current?.play('cancela');
          setOptionsOpen(false);
        }
        return;
      }

      if (event.key === 'o' || event.key === 'O') {
        event.preventDefault();
        setOptionsOpen(true);
        return;
      }

      // B de "batalha automática". Fora do bloco de `over` e do de opções de
      // propósito: é a tecla que devolve o comando ao jogador no meio do turno
      // que a máquina está jogando, e nenhum outro atalho responde ali.
      if (event.key === 'b' || event.key === 'B') {
        event.preventDefault();
        audioRef.current?.play('confirma');
        keyEnvRef.current.game.setAutoBattle(!keyEnvRef.current.game.autoBattle);
        return;
      }

      if (over) {
        /*
         * A tela de fim de capítulo tem a lista dela.
         *
         * Antes o teclado só conhecia as duas saídas da escaramuça, e
         * confirmar no capítulo recomeçava o mapa qualquer que fosse a linha
         * escolhida — inclusive a que dizia "Próximo capítulo".
         */
        if (chapterVerdictOptions.length > 0) {
          const size = chapterVerdictOptions.length;
          if (event.key === 'ArrowUp' || event.key === 'w') {
            event.preventDefault();
            audioRef.current?.unlock();
            audioRef.current?.play('cursor');
            setChapterChoice((c) => (c - 1 + size) % size);
          } else if (event.key === 'ArrowDown' || event.key === 's') {
            event.preventDefault();
            audioRef.current?.unlock();
            audioRef.current?.play('cursor');
            setChapterChoice((c) => (c + 1) % size);
          } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'z') {
            event.preventDefault();
            const option = chapterVerdictOptions[chapterChoice % size];
            if (option) runChapterChoice(option);
          }
          return;
        }
        if (event.key === 'ArrowUp' || event.key === 'w') {
          event.preventDefault();
          audioRef.current?.unlock();
          audioRef.current?.play('cursor');
          setVerdictSelection('restart');
        } else if (event.key === 'ArrowDown' || event.key === 's') {
          event.preventDefault();
          audioRef.current?.unlock();
          audioRef.current?.play('cursor');
          setVerdictSelection('change_map');
        } else if (event.key === 'ArrowLeft' || event.key === 'a' || event.key === 'q') {
          if (verdictSelection === 'change_map') {
            event.preventDefault();
            audioRef.current?.unlock();
            audioRef.current?.play('cursor');
            cycleNextMap(-1);
          }
        } else if (event.key === 'ArrowRight' || event.key === 'd' || event.key === 'e') {
          if (verdictSelection === 'change_map') {
            event.preventDefault();
            audioRef.current?.unlock();
            audioRef.current?.play('cursor');
            cycleNextMap(1);
          }
        } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'z') {
          event.preventDefault();
          audioRef.current?.unlock();
          audioRef.current?.play('confirma');
          if (verdictSelection === 'restart') {
            handleRestart();
          } else {
            handleChangeMap(selectedNextMapId);
          }
        }
        return;
      }

      /*
       * O menu de campo responde com a máquina jogando; o resto, não.
       *
       * Sem esta exceção o teclado ficava inerte durante a batalha automática
       * — inclusive o Esc que abre o menu onde ela se desliga. Em tela cheia
       * isso era um beco sem saída: nenhum botão de página para clicar e
       * nenhuma tecla que respondesse.
       */
      if (game.isCpuTurn || game.isCpuActing) {
        const navegando = game.selection.mode === 'campo';
        if (event.key === 'Escape' || event.key === 'x' || event.key === 'Backspace') {
          event.preventDefault();
          if (navegando) game.cancel();
          else game.openFieldMenu();
        } else if (navegando) {
          event.preventDefault();
          if (event.key === 'ArrowUp' || event.key === 'w') game.moveCursor({ x: 0, y: -1 });
          else if (event.key === 'ArrowDown' || event.key === 's') game.moveCursor({ x: 0, y: 1 });
          else if (event.key === 'ArrowLeft' || event.key === 'a') game.moveCursor({ x: -1, y: 0 });
          else if (event.key === 'ArrowRight' || event.key === 'd') game.moveCursor({ x: 1, y: 0 });
          else if (event.key === 'Enter' || event.key === ' ' || event.key === 'z') game.confirm();
        }
        return;
      }

      const keys: Record<string, () => void> = {
        ArrowUp: () => game.moveCursor({ x: 0, y: -1 }),
        ArrowDown: () => game.moveCursor({ x: 0, y: 1 }),
        ArrowLeft: () => game.moveCursor({ x: -1, y: 0 }),
        ArrowRight: () => game.moveCursor({ x: 1, y: 0 }),
        w: () => game.moveCursor({ x: 0, y: -1 }),
        s: () => game.moveCursor({ x: 0, y: 1 }),
        a: () => game.moveCursor({ x: -1, y: 0 }),
        d: () => game.moveCursor({ x: 1, y: 0 }),
        Enter: () => game.confirm(),
        z: () => game.confirm(),
        ' ': () => game.confirm(),
        // Sair da troca é concluí-la, como no Fire Emblem: o gesto de voltar é o
        // mesmo de fechar a bolsa, e o motor recusa a troca que não é troca.
        Escape: () => {
          if (game.selection.mode === 'livre') {
            game.openFieldMenu();
          } else if (game.selection.mode === 'trocando') {
            game.finishTrade();
          } else {
            game.cancel();
          }
        },
        x: () => (game.selection.mode === 'trocando' ? game.finishTrade() : game.cancel()),
        Backspace: () => (game.selection.mode === 'trocando' ? game.finishTrade() : game.cancel()),
        Tab: () => {
          if (game.selection.mode === 'mirando') {
            game.cycleTarget(1);
          } else {
            game.cycleAlly(1);
          }
        },
        q: () => {
          if (game.selection.mode === 'mirando') {
            game.cycleTarget(-1);
          } else {
            game.cycleAlly(-1);
          }
        },
        e: () => {
          if (game.selection.mode === 'mirando') {
            game.cycleTarget(1);
          } else {
            game.cycleAlly(1);
          }
        },
        f: () => {
          if (game.selection.mode === 'livre' && !over && !game.isCpuTurn && !game.isCpuActing) {
            game.finishPhase();
          }
        },
        End: () => {
          if (game.selection.mode === 'livre' && !over && !game.isCpuTurn && !game.isCpuActing) {
            game.finishPhase();
          }
        },
        /*
         * R acende o alcance do inimigo debaixo do cursor; T, o do exército.
         *
         * Duas teclas porque são duas leituras diferentes: a de um inimigo só
         * é a que resolve "posso parar ao lado deste?", e a de todos é a
         * varredura antes de mover a primeira peça. Uma tecla que fizesse as
         * duas coisas dependeria de onde o cursor estava, e o jogador não olha
         * para o cursor quando aperta um atalho.
         */
        r: () => game.toggleThreatAtCursor(),
        R: () => game.toggleThreatAtCursor(),
        t: () => game.toggleAllThreat(),
        T: () => game.toggleAllThreat(),
        m: () => setMuted((m) => !m),
        // Um botão só percorre os três afastamentos, como o C do Path of Radiance.
        c: () => setZoom((z) => cycleZoom(z)),
        '+': () => setZoom((z) => stepZoom(z, 1)),
        '=': () => setZoom((z) => stepZoom(z, 1)),
        '-': () => setZoom((z) => stepZoom(z, -1)),
      };

      const handler = keys[event.key];
      if (!handler) return;
      event.preventDefault();
      audioRef.current?.unlock();
      handler();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(''), 2600);
    return () => window.clearTimeout(id);
  }, [notice]);

  /**
   * Som no primeiro gesto, seja ele qual for.
   *
   * O navegador só libera áudio depois de uma interação, mas antes disto só
   * clicar no tabuleiro contava: quem chegava pelo menu, pelo seletor de mapa
   * ou pelo teclado jogava em silêncio e achava que o jogo era mudo. Um ouvinte
   * único na janela cobre qualquer gesto, e se remove sozinho depois do
   * primeiro — desbloquear é evento de uma vez só.
   */
  useEffect(() => {
    const events: readonly (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart'];
    const detach = () => {
      for (const type of events) window.removeEventListener(type, onGesture);
    };
    function onGesture(): void {
      // O gesto pode chegar antes do acervo: sem `GameAudio` ainda não há o que
      // destravar, e desistir aqui deixaria a página muda para sempre. Só
      // largamos o ouvinte quando o desbloqueio de fato aconteceu.
      const audio = audioRef.current;
      if (!audio) return;
      audio.unlock();
      audio.playBgm(loadedRef.current?.map.bgm || FALLBACK_BGM);
      setAudioReady(true);
      detach();
    }
    for (const type of events) window.addEventListener(type, onGesture, { passive: true });
    return detach;
  }, []);

  /**
   * Cada mapa com a própria trilha.
   *
   * Antes era uma faixa só para as cinquenta e seis escaramuças, e trocar de
   * mapa não mudava nada no ouvido: o calabouço soava igual à praça da vila. O
   * acervo declara a música em quatro mapas, e o pipeline completa o resto pelo
   * assunto de cada um.
   */
  useEffect(() => {
    audioRef.current?.playBgm(loaded?.map.bgm || FALLBACK_BGM);
  }, [manifest, loaded]);

  useEffect(() => {
    audioRef.current?.setMuted(muted);
  }, [muted]);

  /**
   * A trilha é da batalha, e acaba com ela.
   *
   * O `GameAudio` sobrevivia à saída da tela: quem trocava para a Loja, para a
   * Comunidade ou para o Painel continuava ouvindo a música do calabouço por
   * cima de uma tela que não tinha nada a ver com ela, e não havia como
   * desligar sem voltar para a escaramuça. Sair do campo de batalha desliga o
   * som — voltar monta tudo de novo, que é o que já acontecia com o resto da
   * partida.
   */
  useEffect(() => {
    return () => {
      audioRef.current?.destroy();
      audioRef.current = null;
    };
  }, []);

  /**
   * Tela cheia persistente no document.documentElement e Wake Lock.
   *
   * Ao usar o elemento raiz da página em vez do contêiner do tabuleiro, a tela cheia
   * sobrevive à troca de capítulos, ao recarregamento de mapas e à volta ao menu.
   * A Screen Wake Lock API impede que o computador/celular suspenda ou o monitor desligue.
   */
  useEffect(() => {
    const sync = () => {
      const isFull = Boolean(document.fullscreenElement);
      setFullscreen(isFull);
      document.documentElement.classList.toggle('in-game-fullscreen', isFull);
      if (isFull) {
        void acquireWakeLock();
        void lockLandscapeOrientation();
      } else {
        void releaseWakeLock();
        unlockOrientation();
      }
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  // Segura a tela ativa durante a partida mesmo fora de tela cheia
  useEffect(() => {
    if (fullscreen || game.state.status === 'em_andamento') {
      void acquireWakeLock();
    }
    return () => {
      if (!document.fullscreenElement) {
        void releaseWakeLock();
      }
    };
  }, [fullscreen, game.state.status]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
        unlockOrientation();
      } else {
        const root = document.documentElement;
        if (root.requestFullscreen) {
          await root.requestFullscreen();
        } else if ((root as any).webkitRequestFullscreen) {
          await (root as any).webkitRequestFullscreen();
        }
        await lockLandscapeOrientation();
      }
    } catch {
      setNotice('Este navegador negou a tela cheia.');
    }
  }, []);
  // O menu de campo é montado antes desta função existir; a referência é como
  // ele a alcança sem reconstruir os ganchos a cada quadro.
  toggleFullscreenRef.current = toggleFullscreen;

  // --- interface ------------------------------------------------------------

  /*
   * A tela de abertura vem ANTES de qualquer carregamento.
   *
   * Ela não precisa do acervo nem do tabuleiro — precisa da lista de mapas, que
   * é um pedido leve — e adiá-la até o mapa estar pronto faria a pessoa
   * esperar uma partida que ela talvez nem quisesse começar.
   */
  if (screen === 'titulo') {
    return (
      <TitleScreen
        maps={mapList}
        mapId={mapId}
        onChangeMap={(id) => {
          setRestoring(null);
          setActiveDraft(undefined);
          setMapId(id);
        }}
        onNewGame={() => {
          setRestoring(null);
          setChapter(null);
          reseed();
          setLog([]);
          setScreen('batalha');
        }}
        onContinue={(_slot, save) => resumeSave(save)}
        difficulty={difficulty}
        onChangeDifficulty={changeDifficulty}
        playMode={game.playMode}
        onChangePlayMode={game.setPlayMode}
        cpuSpeed={game.cpuSpeed}
        onChangeCpuSpeed={game.setCpuSpeed}
        confirmButton={confirmButton}
        onChangeConfirmButton={(value) => {
          setConfirmButton(value);
          saveConfirmButton(value);
        }}
        muted={muted}
        onToggleSound={() => {
          audioRef.current?.unlock();
          setMuted((m) => !m);
        }}
        onCursor={() => audioRef.current?.play('cursor')}
        onConfirm={() => {
          audioRef.current?.unlock();
          audioRef.current?.play('confirma');
        }}
      />
    );
  }

  if (error) {
    return (
      <section data-panel="escaramuça" className="skirmish-error">
        <h2>Escaramuça</h2>
        <p className="bad">Não foi possível montar a partida: {error}</p>
        <p className="muted">Rode <code>make assets</code> e recarregue.</p>
      </section>
    );
  }

  if (!manifest || !loaded || !skirmish) {
    return (
      <section data-panel="escaramuça" className="skirmish-loading">
        <h2>Escaramuça</h2>
        <p className="muted">carregando o acervo…</p>
      </section>
    );
  }

  const { state, selection } = game;
  const phaseDone = phaseIsOver(state);
  const shown = game.target ?? game.hovered;
  const shownPresentation = shown ? presentation.get(shown.id) : undefined;
  const shownClass = shown ? loaded.content.classes.get(shown.classId) : undefined;
  const shownWeapon = shown ? weaponOf(loaded.content, shown) : null;

  const hasHudChips = Boolean(
    activeDraft ||
    game.isCpuActing ||
    (game.threatAll || game.threatIds.size > 0) ||
    gamepad.isConnected
  );

  return (
    <section data-panel="escaramuça">
      {hasHudChips && (
        <div className="hud">
          <div className="hud-left">
            {activeDraft && (
              <span className="draftchip" title="Mapa em edição, ainda não gravado no servidor">
                📝 rascunho
              </span>
            )}
            {game.isCpuActing && (
              <span className="cpuchip">
                {game.autoBattle && state.phase === 'jogador'
                  ? 'Auto-batalha jogando…'
                  : 'CPU pensando...'}
              </span>
            )}
            {(game.threatAll || game.threatIds.size > 0) && (
              <button
                type="button"
                className="threatchip"
                onClick={() => game.clearThreat()}
                title="Apagar o alcance inimigo (tecla T · L3 no controle)"
              >
                ☠ alcance inimigo: {game.threatAll ? 'todos' : game.threatIds.size}
                <span className="threatchip-x" aria-hidden="true">✕</span>
              </button>
            )}
            {gamepad.isConnected && (
              <span className="gamepadchip" title={gamepad.gamepadName ?? 'Controle USB/Bluetooth'}>
                🎮 {gamepad.layout === 'nintendo' ? 'Nintendo Switch' : 'Xbox / Padrão'} ativo
              </span>
            )}
          </div>
        </div>
      )}

      <div className={`battlefield${fullscreen ? ' fullscreen' : ''}`} ref={battlefieldRef}>
        <div ref={hostRef} className="boardhost" data-testid="board" />

        <PhaseBanner
          phase={state.phase}
          turn={state.turn}
          active={!over}
          audio={audioRef.current}
          onDone={() => {
            setBannerActive(false);
            bannerActiveRef.current = false;
            boardRef.current?.setBannerActive(false);
          }}
        />

        {/*
          O aviso de que a máquina está jogando — e o botão de tomar o comando.

          Mora DENTRO do campo de batalha, e não na barra da página, porque é
          em tela cheia que ele mais importa: lá a barra fica atrás do elemento
          em tela cheia, e o Esc é engolido pelo navegador para sair dela. Sem
          este botão, ligar a batalha automática em tela cheia era um beco sem
          saída — o jogo seguia sozinho para sempre.
        */}
        {game.isCpuActing && !bannerActive && !over && (
          <div className="autochip" role="status">
            <span className="autochip-dot" aria-hidden="true" />
            <span>
              {game.autoBattle && state.phase === 'jogador'
                ? 'Auto-batalha jogando…'
                : 'CPU pensando…'}
            </span>
            {game.autoBattle && (
              <button
                type="button"
                className="autochip-stop"
                onClick={() => game.setAutoBattle(false)}
                title="Retomar o comando (tecla B · Start no controle)"
              >
                ✋ assumir o comando
              </button>
            )}
          </div>
        )}

        {shown && (() => {
          const board = boardRef.current;
          const mapW = loaded?.map.width ?? 25;
          const mapH = loaded?.map.height ?? 25;

          const toNorm = (t: Vec2) => {
            if (board) {
              const s = board.tileToScreen(t);
              const sz = board.getScreenSize();
              if (sz.width > 0 && sz.height > 0) {
                return { nx: s.x / sz.width, ny: s.y / sz.height };
              }
            }
            return { nx: (t.x + 0.5) / mapW, ny: (t.y + 0.5) / mapH };
          };

          const corners: readonly ('bottom-left' | 'bottom-right' | 'top-left' | 'top-right')[] = [
            'bottom-left',
            'bottom-right',
            'top-left',
            'top-right',
          ];

          const isIn = (p: { nx: number; ny: number }, c: string) => {
            const isL = p.nx <= 0.48;
            const isR = p.nx >= 0.52;
            const isT = p.ny <= 0.52;
            const isB = p.ny >= 0.48;
            if (c === 'bottom-left') return isL && isB;
            if (c === 'bottom-right') return isR && isB;
            if (c === 'top-left') return isL && isT;
            return isR && isT;
          };

          const cPos = toNorm(game.cursor);
          const sPos = toNorm(shown.position);
          const selUnit = game.selection.mode === 'movendo' ? unitById(game.state, game.selection.unitId) : null;
          const selPos = selUnit ? toNorm(selUnit.position) : null;

          const living = livingUnits(game.state);
          const unitPositions = living.map((u) => toNorm(u.position));

          let bestCorner: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' = 'bottom-left';
          let minPenalty = Infinity;

          for (const c of corners) {
            let penalty = 0;
            // O cursor está neste canto: desvia a caixa para não cobrir a mira/seleção
            if (isIn(cPos, c)) penalty += 60;
            // A própria unidade inspecionada está neste canto
            if (isIn(sPos, c)) penalty += 45;
            // A unidade sendo movida está neste canto
            if (selPos && isIn(selPos, c)) penalty += 45;

            // Qualquer outro personagem no tabuleiro que ficaria oculto sob a caixa
            for (const up of unitPositions) {
              if (isIn(up, c)) penalty += 15;
            }

            // Leve preferência pelos cantos inferiores (ergonomia visual GBA)
            if (c === 'top-left' || c === 'top-right') {
              penalty += 2;
            }

            if (penalty < minPenalty) {
              minPenalty = penalty;
              bestCorner = c;
            }
          }

          return (
            <aside className={`window unitcard unitcard-${bestCorner}`}>
              {shownPresentation &&
                // A criatura mostra a si mesma; quem tem rosto mostra o rosto.
                (shownPresentation.battlerAsPortrait ? (
                  <BattlerPortrait manifest={manifest} battler={shownPresentation.battler} />
                ) : (
                  <Portrait
                    manifest={manifest}
                    sheet={shownPresentation.portrait.sheet}
                    index={shownPresentation.portrait.index}
                  />
                ))}
              <div className="unitcard-body">
                <h3>{shown.name}</h3>
                <p className="unitclass">
                  {shownClass?.name ?? '—'} · nível {shown.level}
                  <span className={`teamdot team-${shown.team}`} />
                </p>
                <HpBar current={shown.currentHp} max={shown.stats.hp} team={shown.team} />
                <ConditionChips unit={shown} />
                <div className="unitcard-exp">
                  <div className="unitcard-exp-row">
                    <span className="unitcard-exp-title">EXP</span>
                    <span className="unitcard-exp-num">{shown.exp} / 100</span>
                  </div>
                  <div className="exp-bar">
                    <div className="exp-bar-fill" style={{ width: `${Math.min(100, shown.exp)}%` }} />
                  </div>
                </div>
                <dl className="statgrid">
                  <div className="stat-chip"><dt>For</dt><dd>{shown.stats.str}</dd></div>
                  <div className="stat-chip"><dt>Mag</dt><dd>{shown.stats.mag}</dd></div>
                  <div className="stat-chip"><dt>Des</dt><dd>{shown.stats.skl}</dd></div>
                  <div className="stat-chip"><dt>Vel</dt><dd>{shown.stats.spd}</dd></div>
                  <div className="stat-chip"><dt>Sor</dt><dd>{shown.stats.lck}</dd></div>
                  <div className="stat-chip"><dt>Def</dt><dd>{shown.stats.def}</dd></div>
                  <div className="stat-chip"><dt>Res</dt><dd>{shown.stats.res}</dd></div>
                  <div className="stat-chip"><dt>Mov</dt><dd>{shownClass?.move ?? '—'}</dd></div>
                </dl>
                {shownWeapon && (
                  <div className="unitcard-weapon">
                    <ItemIcon
                      icon={(shownWeapon as any)?.icon}
                      iconSheet={(shownWeapon as any)?.iconSheet}
                      type={shownWeapon.type}
                      size="1.6em"
                    />
                    <div className="unitcard-weapon-info">
                      <div className="unitcard-weapon-name-row">
                        <span className="unitcard-weapon-name">{shownWeapon.name}</span>
                        <span className="unitcard-weapon-uses">
                          {shown.inventory[shown.equipped]?.usesLeft ?? 0} usos
                        </span>
                      </div>
                      <span className="unitcard-weapon-stats">
                        {shownWeapon.heals ? 'Cura' : 'Poder'} {shownWeapon.might} · Alc {shownWeapon.range.join('–')}
                      </span>
                    </div>
                  </div>
                )}
                {(() => {
                  // O que o golpe desta unidade pode deixar — da arma e da
                  // criatura juntas, como o motor sorteia.
                  const text = afflictionText(afflictionsOf(combatantOf(state, loaded.content, shown)));
                  return text ? <p className="unitcard-afflicts">Golpe: {text}</p> : null;
                })()}
                {/*
                  A dica só aparece sobre quem joga contra você, e só de mãos
                  livres.

                  É onde o jogador está olhando quando a pergunta aparece — ele
                  já parou o cursor no inimigo para ler os atributos —, e é o
                  único lugar em que cabe dizer que o gesto existe sem virar
                  mais uma linha de legenda permanente na tela. Com uma unidade
                  já levantada, porém, o mesmo Enter faz outra coisa: anda até
                  lá, ou bate. A tecla R continua valendo; prometer as três é
                  que seria mentira.
                */}
                {game.selection.mode === 'livre' && isHostile(state.phase, shown.team) && (
                  <p className={`unitcard-threat${game.threatIds.has(shown.id) ? ' on' : ''}`}>
                    {game.threatIds.has(shown.id) ? '☠ alcance à mostra' : '☠ ver alcance'}
                    <span className="unitcard-threat-key">Enter · clique · R</span>
                  </p>
                )}
              </div>
            </aside>
          );
        })()}

        {selection.mode === 'campo' && (
          <nav className="window actionmenu fieldmenu" aria-label="Ordens de campo">
            <header className="fieldmenu-head">
              Turno {state.turn} · fase {state.phase}
            </header>
            {game.fieldOptions.map((opt, i) => {
              const isActive = i === selection.index;
              const busy = game.isCpuTurn || game.isCpuActing;
              // Durante a jogada da máquina o menu abre, mas só as ordens de
              // tela respondem: encerrar a fase ou abrir as opções no meio de
              // um turno alheio não é uma ordem, é um acidente.
              const disabled = busy && (opt === 'encerrar_fase' || opt === 'opcoes' || opt === 'ameaca');
              const label =
                opt === 'encerrar_fase'
                  ? `Encerrar fase${phaseDone ? ' ✓' : ''}`
                  : opt === 'ameaca'
                  ? `☠ Alcance inimigo: ${
                      game.threatAll
                        ? 'todos'
                        : game.threatIds.size > 0
                        ? `${game.threatIds.size} marcado${game.threatIds.size > 1 ? 's' : ''}`
                        : 'desligado'
                    }`
                  : opt === 'auto_fase'
                  ? `🤖 Auto-batalha: ${game.autoBattle ? 'ligada' : 'desligada'}`
                  : opt === 'camera'
                  ? `🔍 Câmera: ${zoomLevel(zoom).label}`
                  : opt === 'som'
                  ? `${muted ? '🔇' : '🔊'} Som: ${muted ? 'desligado' : 'ligado'}`
                  : opt === 'tela_cheia'
                  ? `⛶ Tela cheia: ${fullscreen ? 'sim' : 'não'}`
                  : opt === 'opcoes'
                  ? '⚙ Opções da Partida'
                  : 'Voltar';
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={disabled}
                  className={`${opt === 'voltar' ? 'ghost ' : ''}${isActive ? 'menu-item-active' : ''}${
                    opt === 'auto_fase' && game.autoBattle ? ' fieldmenu-on' : ''
                  }${
                    opt === 'ameaca' && (game.threatAll || game.threatIds.size > 0)
                      ? ' fieldmenu-on fieldmenu-on-threat'
                      : ''
                  }`}
                  onMouseEnter={() => game.moveCursor({ x: 0, y: i - selection.index })}
                  onClick={() => game.chooseField(opt)}
                >
                  {isActive && <span className="menu-bullet">▶ </span>}
                  {label}
                </button>
              );
            })}
          </nav>
        )}

        {selection.mode === 'menu' && (
          <nav className="window actionmenu">
            {game.menuOptions.map((opt, i) => {
              const isActive = i === game.menuIndex;
              const labels: Record<string, string> = {
                atacar: 'Atacar',
                conversar: '💬 Conversar',
                curar: 'Curar',
                bau: '🧰 Abrir baú',
                itens: 'Itens',
                trocar: 'Trocar',
                esperar: 'Esperar',
                auto: '🤖 Auto',
                voltar: 'Voltar',
              };
              const isGhost = opt === 'voltar';

              return (
                <button
                  key={opt}
                  type="button"
                  className={`${isGhost ? 'ghost ' : ''}${isActive ? 'menu-item-active' : ''}`}
                  onMouseEnter={() => game.setMenuIndex(i)}
                  onClick={() => {
                    game.setMenuIndex(i);
                    if (opt === 'voltar') game.cancel();
                    else game.chooseMenu(opt);
                  }}
                >
                  {isActive && <span className="menu-bullet">▶ </span>}
                  {labels[opt] ?? opt}
                </button>
              );
            })}
          </nav>
        )}

        {selection.mode === 'itens' && (() => {
          const unit = unitById(state, selection.unitId);
          if (!unit || !loaded) return null;
          return (
            <nav className="window itemmenu" aria-label="Inventário da unidade">
              <header className="itemmenu-head">
                <span className="itemmenu-head-title">🎒 Bolsa de {unit.name}</span>
                <span className="itemmenu-head-count">
                  {unit.inventory.length} / {MAX_INVENTORY}
                </span>
              </header>
              <div className="itemmenu-list">
                {unit.inventory.map((item, i) => {
                  const view = slotView(loaded.content, loaded, item);
                  const isActive = i === selection.index;
                  const isEquipped = i === unit.equipped && view.kind === 'arma';
                  return (
                    <button
                      key={`${view.kind}-${item.weaponId ?? item.itemId}-${i}`}
                      type="button"
                      className={`itemrow ${isActive ? 'menu-item-active' : ''} ${isEquipped ? 'itemrow-equipped-box' : ''}`}
                      onMouseEnter={() => game.moveCursor({ x: 0, y: i - selection.index })}
                      onClick={() => game.chooseItem(i)}
                    >
                      <div className="itemrow-left">
                        {isActive && <span className="menu-bullet">▶ </span>}
                        <ItemIcon
                          icon={view.icon}
                          iconSheet={view.iconSheet}
                          type={view.type}
                          size="1.6em"
                        />
                        <div className="itemrow-label">
                          <span className="itemrow-name">{view.name}</span>
                          <span className="itemrow-stats">{view.detail}</span>
                        </div>
                      </div>
                      <div className="itemrow-right">
                        {view.usable ? (
                          <span className="itemrow-use-badge">USAR</span>
                        ) : (
                          isEquipped && <span className="itemrow-equipped-badge">EQUIPADA</span>
                        )}
                        <span className="itemrow-uses-pill" title="Usos restantes">
                          ⚡ {item.usesLeft}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="itemmenu-note">
                Equipar não consome o turno; usar um item, sim — nele mesmo ou num aliado ao lado.
              </p>
            </nav>
          );
        })()}

        {selection.mode === 'trocando' && (() => {
          const unit = unitById(state, selection.unitId);
          const partner = unitById(state, selection.targetId);
          if (!unit || !partner || !loaded) return null;

          const column = (
            side: 'propria' | 'alheia',
            owner: typeof unit,
            bag: readonly ItemInstance[],
          ): JSX.Element => (
            <div
              className={`trade-column ${selection.side === side ? 'trade-column-active' : ''}`}
              onMouseEnter={() => game.setTradeSide(side)}
            >
              <header className="trade-column-head">
                <span className="trade-owner">{owner.name}</span>
                <span className="trade-count">
                  {bag.length} / {MAX_INVENTORY}
                </span>
              </header>
              <div className="trade-list">
                {bag.length === 0 && <p className="trade-empty">bolsa vazia</p>}
                {bag.map((item, i) => {
                  const view = slotView(loaded.content, loaded, item);
                  const isActive = selection.side === side && i === selection.index;
                  return (
                    <button
                      key={`${view.kind}-${item.weaponId ?? item.itemId}-${i}`}
                      type="button"
                      className={`trade-item ${isActive ? 'menu-item-active' : ''}`}
                      onMouseEnter={() => game.setTradeSide(side)}
                      onClick={() => game.moveTradeItem(side, i)}
                      title={
                        side === 'propria'
                          ? `Passar para ${partner.name}`
                          : `Pegar de ${partner.name}`
                      }
                    >
                      {isActive && <span className="menu-bullet">▶ </span>}
                      <ItemIcon
                        icon={view.icon}
                        iconSheet={view.iconSheet}
                        type={view.type}
                        size="1.5em"
                      />
                      <span className="trade-item-label">
                        <span className="trade-item-name">{view.name}</span>
                        <span className="trade-item-uses">⚡ {item.usesLeft}</span>
                      </span>
                      <span className="trade-item-arrow">{side === 'propria' ? '▶' : '◀'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );

          return (
            <div className="window trademenu" role="dialog" aria-label="Troca de itens">
              <header className="trade-head">
                🤝 Troca — {unit.name} &amp; {partner.name}
              </header>
              <div className="trade-columns">
                {column('propria', unit, selection.mine)}
                <div className="trade-divider" aria-hidden="true">⇄</div>
                {column('alheia', partner, selection.theirs)}
              </div>
              <div className="trade-actions">
                <button type="button" className="trade-done" onClick={() => game.finishTrade()}>
                  Concluir ✓
                </button>
                <button type="button" className="ghost trade-cancel" onClick={() => game.resetTrade()}>
                  ↺ Desfazer
                </button>
              </div>
              <p className="trade-note">
                Enter / A passa o item para a outra bolsa · ◀ ▶ trocam de coluna ·
                Esc / B conclui. Uma troca de fato encerra o turno de {unit.name};
                sair sem mexer em nada, não.
              </p>
            </div>
          );
        })()}

        {game.forecast && game.target && (
          <div className="window forecast">
            {game.selection.mode === 'mirando' && game.selection.targets.length > 1 && (
              <div className="forecast-target-switcher">
                <button
                  type="button"
                  className="target-nav-btn"
                  onClick={() => game.cycleTarget(-1)}
                  title="Alvo anterior (Analógico / D-Pad / Q / Y / LB)"
                >
                  ◀
                </button>
                <span className="target-counter">
                  Alvo {game.selection.index + 1} de {game.selection.targets.length}
                </span>
                <button
                  type="button"
                  className="target-nav-btn"
                  onClick={() => game.cycleTarget(1)}
                  title="Próximo alvo (Analógico / D-Pad / E / X / RB)"
                >
                  ▶
                </button>
                <span className="target-hint">(D-Pad / Analógico / X / Y)</span>
              </div>
            )}
            <div className="forecast-side">
              <strong>{unitById(state, selection.mode === 'mirando' ? selection.unitId : 0)?.name}</strong>
              <b>{game.forecast.attacker.damage}</b>
              <span>{game.forecast.attacker.hit}% acerto</span>
              <span>{game.forecast.attacker.crit}% crítico</span>
              {game.forecast.attacker.attacks > 1 && <em>×2</em>}
              {game.forecast.attacker.triangle !== 'neutro' && (
                <i className={`tri ${game.forecast.attacker.triangle}`}>
                  {game.forecast.attacker.triangle === 'vantagem' ? '▲ vantagem' : '▼ desvantagem'}
                </i>
              )}
              {game.forecast.attacker.inflicts && (
                <span className="forecast-afflicts">{afflictionText(game.forecast.attacker.inflicts)}</span>
              )}
            </div>
            <div className="forecast-vs">vs</div>
            <div className="forecast-side">
              <strong>{game.target.name}</strong>
              <b>{game.forecast.defender.canAttack ? game.forecast.defender.damage : '—'}</b>
              <span>{game.forecast.defender.canAttack ? `${game.forecast.defender.hit}% acerto` : 'não revida'}</span>
              <span>{game.forecast.defender.canAttack ? `${game.forecast.defender.crit}% crítico` : ''}</span>
              {game.forecast.defender.attacks > 1 && <em>×2</em>}
              {game.forecast.defender.canAttack && game.forecast.defender.inflicts && (
                <span className="forecast-afflicts">{afflictionText(game.forecast.defender.inflicts)}</span>
              )}
              <ConditionChips unit={game.target} />
            </div>
            <button type="button" className="confirmattack" onClick={game.confirm}>Atacar</button>
          </div>
        )}

        {selection.mode === 'mirando' && selection.kind === 'item' && game.target && (() => {
          const user = unitById(state, selection.unitId);
          const slot = user?.inventory[selection.itemIndex ?? 0];
          const use = itemUseOf(loaded.content, slot);
          if (!user || !slot || !use) return null;
          const view = slotView(loaded.content, loaded, slot);
          const heal = itemHealFor(use, game.target);
          const cures = curableBy(game.target.states, use.cures);
          return (
            <div className="window forecast itemforecast">
              {selection.targets.length > 1 && (
                <div className="forecast-target-switcher">
                  <button type="button" className="target-nav-btn" onClick={() => game.cycleTarget(-1)}>◀</button>
                  <span className="target-counter">
                    Alvo {selection.index + 1} de {selection.targets.length}
                  </span>
                  <button type="button" className="target-nav-btn" onClick={() => game.cycleTarget(1)}>▶</button>
                </div>
              )}
              <div className="forecast-side">
                <strong>{view.name}</strong>
                <span>{describeUse(use)}</span>
              </div>
              <div className="forecast-vs">→</div>
              <div className="forecast-side">
                <strong>{game.target.id === user.id ? `${user.name} (em si)` : game.target.name}</strong>
                {heal > 0 && <b>+{heal} PV</b>}
                {cures.length > 0 && <span>cura {conditionList(cures).toLowerCase()}</span>}
                <ConditionChips unit={game.target} />
              </div>
              <button type="button" className="confirmattack" onClick={game.confirm}>Usar</button>
            </div>
          );
        })()}

        {/* A conversa que muda alguém de lado: a confirmação diz com quem se
            vai falar e o que acontece, porque a ordem NÃO tem previsão de
            combate — e uma ordem sem previsão nenhuma pareceria um engano. */}
        {selection.mode === 'mirando' && selection.kind === 'conversar' && game.target && (() => {
          const talker = unitById(state, selection.unitId);
          if (!talker) return null;
          return (
            <div className="window forecast talkforecast">
              {selection.targets.length > 1 && (
                <div className="forecast-target-switcher">
                  <button type="button" className="target-nav-btn" onClick={() => game.cycleTarget(-1)}>◀</button>
                  <span className="target-counter">
                    Alvo {selection.index + 1} de {selection.targets.length}
                  </span>
                  <button type="button" className="target-nav-btn" onClick={() => game.cycleTarget(1)}>▶</button>
                </div>
              )}
              <div className="forecast-side">
                <strong>{talker.name}</strong>
                <span>quer conversar</span>
              </div>
              <div className="forecast-vs">💬</div>
              <div className="forecast-side">
                <strong>{game.target.name}</strong>
                <span>entra para o esquadrão</span>
                <ConditionChips unit={game.target} />
              </div>
              <button type="button" className="confirmattack" onClick={game.confirm}>Conversar</button>
            </div>
          );
        })()}

        {/* Com quem trocar, quando há mais de um companheiro encostado.
            A troca não tem previsão de combate, então a confirmação diz o que
            diria a barra: quem propõe, quem recebe, e o que cada um carrega. */}
        {selection.mode === 'mirando' && selection.kind === 'trocar' && game.target && (() => {
          const owner = unitById(state, selection.unitId);
          if (!owner) return null;
          const bag = (unit: typeof owner): string =>
            unit.inventory.length === 0
              ? 'bolsa vazia'
              : `${unit.inventory.length} de ${MAX_INVENTORY} espaços`;
          return (
            <div className="window forecast tradeforecast">
              {selection.targets.length > 1 && (
                <div className="forecast-target-switcher">
                  <button type="button" className="target-nav-btn" onClick={() => game.cycleTarget(-1)}>◀</button>
                  <span className="target-counter">
                    Companheiro {selection.index + 1} de {selection.targets.length}
                  </span>
                  <button type="button" className="target-nav-btn" onClick={() => game.cycleTarget(1)}>▶</button>
                  <span className="target-hint">(D-Pad / Analógico / X / Y)</span>
                </div>
              )}
              <div className="forecast-side">
                <strong>{owner.name}</strong>
                <span>{bag(owner)}</span>
              </div>
              <div className="forecast-vs">⇄</div>
              <div className="forecast-side">
                <strong>{game.target.name}</strong>
                <span>{bag(game.target)}</span>
                <ConditionChips unit={game.target} />
              </div>
              <button type="button" className="confirmattack" onClick={game.confirm}>Trocar</button>
            </div>
          );
        })()}

        {notice && <div className="notice">{notice}</div>}

        {/* A conversa termina antes do veredito: recrutar o último inimigo
            vence o mapa, e as duas telas chegavam juntas — a fala do recruta
            por cima da tela de vitória. */}
        {over && !chapter && !cutscene && !recruitScene && (() => {
          const living = livingUnits(state);
          const allyCount = living.filter((u) => u.team === 'jogador').length;
          const enemyCount = living.filter((u) => u.team === 'inimigo').length;
          const selectedNextMap = mapList.find((m) => m.id === selectedNextMapId) ?? mapList[0];
          const currentMapName = mapList.find((m) => m.id === mapId)?.name ?? `Mapa #${mapId}`;

          return (
            <div
              className={`verdict-backdrop ${state.status === 'vitoria' ? 'verdict-vitoria' : 'verdict-derrota'}`}
              role="dialog"
              aria-modal="true"
              aria-label={state.status === 'vitoria' ? 'Vitória' : 'Derrota'}
            >
              <div className="verdict-rays" />
              <div className="verdict-particles" />

              <div className="window verdict-modal">
                <div className="verdict-banner">
                  <div className="verdict-crest">
                    {state.status === 'vitoria' ? '👑' : '⚔️'}
                  </div>
                  <h2 className="verdict-title">
                    {state.status === 'vitoria' ? 'VITÓRIA' : 'DERROTA'}
                  </h2>
                  <p className="verdict-subtitle">
                    {state.status === 'vitoria'
                      ? 'O comandante inimigo sucumbiu! Triunfo glorioso no campo de batalha!'
                      : 'O esquadrão aliado caiu em combate... A batalha foi perdida.'}
                  </p>
                </div>

                {reward !== null && (
                  <p className="verdict-reward">
                    ◈ <strong>+{reward}</strong> Emblemas creditados na sua carteira
                  </p>
                )}
                {unlockedRecruits.length > 0 && (
                  <div className="verdict-unlocks">
                    <p className="unlocks-title">✨ <strong>Novos Personagens Desbloqueados:</strong></p>
                    <div className="unlocks-list">
                      {unlockedRecruits.map((u) => (
                        <div key={u.characterId} className="unlocked-unit-badge">
                          🛡️ <strong>{u.name}</strong> (Nível {u.level}) agora está disponível no seu esquadrão!
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {grantedItems.length > 0 && (
                  <div className="verdict-items">
                    <p className="items-title">📦 <strong>Novos Itens Obtidos:</strong></p>
                    <div className="items-list">
                      {grantedItems.map((it, idx) => (
                        <span key={idx} className="granted-item-badge">
                          +{it.quantity}x {it.name || (it.type === 'weapons' ? `Arma #${it.id}` : `Item #${it.id}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* O capítulo tem tela própria (`chapter-verdict`); esta é a
                    da escaramuça solta, e aqui não há capítulo nenhum. */}

                <div className="verdict-stats">
                  <div className="verdict-stat-pill">
                    <span className="stat-label">Turnos</span>
                    <span className="stat-val">{state.turn}</span>
                  </div>
                  <div className="verdict-stat-pill">
                    <span className="stat-label">Aliados de Pé</span>
                    <span className="stat-val stat-ally">{allyCount}</span>
                  </div>
                  <div className="verdict-stat-pill">
                    <span className="stat-label">Inimigos Restantes</span>
                    <span className="stat-val stat-enemy">{enemyCount}</span>
                  </div>
                </div>

                <div className="verdict-options">
                  <button
                    type="button"
                    className={`verdict-btn ${verdictSelection === 'restart' ? 'verdict-btn-active' : ''}`}
                    onMouseEnter={() => {
                      setVerdictSelection('restart');
                      audioRef.current?.play('cursor');
                    }}
                    onClick={() => {
                      audioRef.current?.unlock();
                      audioRef.current?.play('confirma');
                      handleRestart();
                    }}
                  >
                    <span className="verdict-btn-bullet">{verdictSelection === 'restart' ? '▶' : '•'}</span>
                    <span className="verdict-btn-content">
                      <span className="verdict-btn-title">↺ Jogar de novo</span>
                      <span className="verdict-btn-desc">Recomeçar no mapa atual ({currentMapName})</span>
                    </span>
                  </button>

                  <div
                    className={`verdict-map-box ${verdictSelection === 'change_map' ? 'verdict-btn-active' : ''}`}
                    onMouseEnter={() => {
                      setVerdictSelection('change_map');
                      audioRef.current?.play('cursor');
                    }}
                  >
                    <button
                      type="button"
                      className="verdict-btn verdict-btn-map"
                      onClick={() => {
                        audioRef.current?.unlock();
                        audioRef.current?.play('confirma');
                        handleChangeMap(selectedNextMapId);
                      }}
                    >
                      <span className="verdict-btn-bullet">{verdictSelection === 'change_map' ? '▶' : '•'}</span>
                      <span className="verdict-btn-content">
                        <span className="verdict-btn-title">🗺 Trocar de mapa</span>
                        <span className="verdict-btn-desc">Iniciar nova partida no mapa selecionado</span>
                      </span>
                    </button>

                    <div className="verdict-map-picker">
                      <button
                        type="button"
                        className="verdict-map-nav-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          audioRef.current?.unlock();
                          audioRef.current?.play('cursor');
                          cycleNextMap(-1);
                        }}
                        title="Mapa anterior (Joystick: Esquerda / Teclado: ←)"
                      >
                        ◀
                      </button>
                      <span className="verdict-map-name" title={`Selecionado: ${selectedNextMap?.name}`}>
                        #{selectedNextMap?.id ?? selectedNextMapId} — {selectedNextMap?.name ?? `Mapa ${selectedNextMapId}`}
                      </span>
                      <button
                        type="button"
                        className="verdict-map-nav-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          audioRef.current?.unlock();
                          audioRef.current?.play('cursor');
                          cycleNextMap(1);
                        }}
                        title="Próximo mapa (Joystick: Direita / Teclado: →)"
                      >
                        ▶
                      </button>
                    </div>
                  </div>
                </div>

                <div className="verdict-controls-hint">
                  <span>🎮 <b>D-Pad / Analógico</b>: Escolher • <b>Botão A</b>: Confirmar • <b>◀ / ▶</b>: Mudar Mapa</span>
                  <span>⌨ <b>↑ / ↓</b>: Escolher • <b>Enter / Espaço</b>: Confirmar • <b>← / →</b>: Mudar Mapa</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/*
          O desfecho de um CAPÍTULO não é o desfecho de uma escaramuça.

          A escaramuça acaba oferecendo outro mapa, porque é o que ela é: uma
          partida solta. Um capítulo acaba oferecendo o capítulo seguinte — e
          é isso que faz a campanha ser uma campanha. Antes os dois caíam na
          mesma tela, e vencer o Capítulo 1 terminava num seletor de mapas
          que não tinha nada a ver com a história.
        */}
        {over && chapter && !cutscene && !recruitScene && (() => {
          const won = state.status === 'vitoria';
          const living = livingUnits(state);
          const allyCount = living.filter((u) => u.team === 'jogador').length;

          return (
            <div
              className={`verdict-backdrop ${won ? 'verdict-vitoria' : 'verdict-derrota'}`}
              role="dialog"
              aria-modal="true"
              aria-label={won ? 'Capítulo concluído' : 'Capítulo perdido'}
            >
              <div className="verdict-rays" />

              <div className="window verdict-modal chapter-verdict">
                <div className="verdict-banner">
                  <div className="verdict-crest">{won ? '📖' : '⚔️'}</div>
                  <h2 className="verdict-title">{won ? 'CAPÍTULO CONCLUÍDO' : 'DERROTA'}</h2>
                  <p className="verdict-subtitle">{chapter.title}</p>
                </div>

                {reward !== null && (
                  <p className="verdict-reward">
                    ◈ <strong>+{reward}</strong> Emblemas creditados na sua carteira
                  </p>
                )}
                {unlockedRecruits.length > 0 && (
                  <div className="verdict-unlocks">
                    <p className="unlocks-title">✨ <strong>Novos Personagens:</strong></p>
                    <div className="unlocks-list">
                      {unlockedRecruits.map((u) => (
                        <div key={u.characterId} className="unlocked-unit-badge">
                          🛡️ <strong>{u.name}</strong> (Nível {u.level}) entra no seu esquadrão!
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {grantedItems.length > 0 && (
                  <div className="verdict-items">
                    <p className="items-title">📦 <strong>Itens obtidos:</strong></p>
                    <div className="items-list">
                      {grantedItems.map((it, idx) => (
                        <span key={idx} className="granted-item-badge">
                          +{it.quantity}x {it.name || (it.type === 'weapons' ? `Arma #${it.id}` : `Item #${it.id}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="verdict-stats">
                  <div className="verdict-stat-pill">
                    <span className="stat-label">Turnos</span>
                    <span className="stat-val">{state.turn}</span>
                  </div>
                  <div className="verdict-stat-pill">
                    <span className="stat-label">Aliados de Pé</span>
                    <span className="stat-val stat-ally">{allyCount}</span>
                  </div>
                  <div className="verdict-stat-pill">
                    <span className="stat-label">Objetivo</span>
                    <span className="stat-val">{objectiveLabel(chapter.objective)}</span>
                  </div>
                </div>

                {/* As três saídas saem da MESMA lista que o teclado e o
                    controle percorrem (`chapterVerdictOptions`): o mouse
                    escolhe apontando, e os outros dois com a seta — mas o que
                    cada linha faz é decidido num lugar só. */}
                <div className="verdict-options">
                  {chapterVerdictOptions.map((option, index) => {
                    const active = index === chapterChoice % Math.max(1, chapterVerdictOptions.length);
                    const label =
                      option === 'proximo'
                        ? { title: '➡ Próximo capítulo', desc: nextChapter?.title ?? '' }
                        : option === 'rejogar'
                          ? {
                              title: `↺ ${won ? 'Rejogar' : 'Tentar de novo'}`,
                              desc: 'Refazer este capítulo do começo',
                            }
                          : { title: '📖 Voltar à campanha', desc: 'Ver a lista de capítulos' };
                    return (
                      <button
                        key={option}
                        type="button"
                        className={`verdict-btn ${active ? 'verdict-btn-active' : ''}`}
                        onMouseEnter={() => {
                          setChapterChoice(index);
                          audioRef.current?.play('cursor');
                        }}
                        onClick={() => runChapterChoice(option)}
                      >
                        <span className="verdict-btn-bullet">{active ? '▶' : '•'}</span>
                        <span className="verdict-btn-content">
                          <span className="verdict-btn-title">{label.title}</span>
                          <span className="verdict-btn-desc">{label.desc}</span>
                        </span>
                      </button>
                    );
                  })}

                  {won && !nextChapter && (
                    <p className="verdict-chapter">
                      Este era o último capítulo da campanha. 🏆
                    </p>
                  )}
                </div>

                <div className="verdict-controls-hint">
                  <span>🎮 <b>D-Pad / Analógico</b>: Escolher • <b>Botão A</b>: Confirmar</span>
                  <span>⌨ <b>↑ / ↓</b>: Escolher • <b>Enter / Espaço</b>: Confirmar</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* As cenas do capítulo — a abertura, e o que se diz ao fim dele. */}
        {cutscene && (
          <CutscenePlayer
            scenes={cutscene}
            onFinish={() => setCutscene(null)}
            confirmButton={confirmButton}
          />
        )}

        {/* A conversa que acabou de trazer alguém para o esquadrão. */}
        {recruitScene && (
          <RecruitScene
            name={recruitScene.name}
            text={recruitScene.text}
            confirmButton={confirmButton}
            onClose={() => setRecruitScene(null)}
          />
        )}

        {/* Guardar a partida: três espaços, mais o automático de cada turno. */}
        {saveMenuOpen && (
          <div
            // Acima do modal de opções, que é de onde ela é aberta: os dois
            // dividem a mesma moldura, e o de baixo no documento ficava por
            // cima do de cima na tela.
            className="in-game-modal-backdrop savemenu-backdrop"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSaveMenuOpen(false);
            }}
          >
            <div className="window in-game-options-modal" role="dialog" aria-modal="true" aria-label="Guardar partida">
              <header className="options-modal-header">
                <h3>💾 Guardar partida</h3>
                <button
                  type="button"
                  className="options-close-x"
                  onClick={() => setSaveMenuOpen(false)}
                  title="Fechar"
                >
                  ✕
                </button>
              </header>

              <div className="options-modal-body">
                <p className="muted savemenu-note">
                  A partida fica guardada NESTE navegador. O que a campanha guarda no
                  servidor — esquadrão e capítulos vencidos — continua sendo do servidor.
                </p>
                <ul className="savelist">
                  {Array.from({ length: SAVE_SLOTS }, (_, i) => i + 1).map((slot) => {
                    const existing = saveSlots.find((entry) => entry.slot === slot)?.save;
                    return (
                      <li key={slot} className="savelist-row">
                        <button
                          type="button"
                          className="savelist-load"
                          onClick={() => {
                            storeSave(slot);
                            setSaveMenuOpen(false);
                          }}
                        >
                          <span className="savelist-slot">ESPAÇO {slot}</span>
                          <span className="savelist-body">
                            <span className="savelist-title">
                              {existing ? saveLabel(existing) : 'vazio — guardar aqui'}
                            </span>
                            {existing && (
                              <span className="savelist-meta">
                                {saveWhen(existing)} · gravar por cima
                              </span>
                            )}
                          </span>
                        </button>
                        {existing && (
                          <button
                            type="button"
                            className="savelist-delete"
                            title="Apagar esta gravação"
                            onClick={() => {
                              deleteSave(slot);
                              refreshSaves();
                            }}
                          >
                            🗑
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {readSave(AUTO_SLOT) && (
                  <p className="muted savemenu-note">
                    Gravação automática: {saveLabel(readSave(AUTO_SLOT)!)} ·{' '}
                    {saveWhen(readSave(AUTO_SLOT)!)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeCombat && manifest && loaded && (() => {
          const attacker = unitById(activeCombat.before, activeCombat.event.attackerId);
          const defender = unitById(activeCombat.before, activeCombat.event.defenderId);
          if (!attacker || !defender) return null;

          const defaultPres: UnitPresentation = {
            portrait: { sheet: 'Distortidus', index: 0 },
            sprite: { sheet: 'distortidus', index: 0 },
            battler: 'distortidus',
          };
          const attackerPres = presentation.get(attacker.id) ?? defaultPres;
          const defenderPres = presentation.get(defender.id) ?? defaultPres;
          const attackerWeapon = weaponOf(loaded.content, attacker);
          const defenderWeapon = weaponOf(loaded.content, defender);
          const map = activeCombat.before.map;
          const tileIndex = defender.position.y * map.width + defender.position.x;
          const terrainType = map.terrainTypes[map.tiles[tileIndex] ?? 0];
          const reach = spanDistance(attacker.position, attacker.size, defender.position, defender.size);
          const combatForecast =
            activeCombat.event.forecast ??
            forecast(
              loaded.content,
              combatantOf(activeCombat.before, loaded.content, attacker, attacker.position),
              combatantOf(activeCombat.before, loaded.content, defender, defender.position),
              reach,
            );

          return (
            <CombatScene
              manifest={manifest}
              attacker={attacker}
              defender={defender}
              attackerPres={attackerPres}
              defenderPres={defenderPres}
              attackerWeapon={attackerWeapon}
              defenderWeapon={defenderWeapon}
              forecast={combatForecast}
              blows={activeCombat.event.blows}
              audio={audioRef.current}
              terrainType={terrainType}
              battleback={loaded.map.battleback}
              battleback2={loaded.map.battleback2}
              onComplete={() => {
                const res = activeCombat.resolve;
                setActiveCombat(null);
                res();
              }}
            />
          );
        })()}

        {/* Modal de Opções In-Game renderizado diretamente no jogo */}
        {optionsOpen && (
          <div
            className="in-game-modal-backdrop"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOptionsOpen(false);
            }}
          >
            <div
              className="window in-game-options-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Opções da Partida"
            >
              <header className="options-modal-header">
                <h3>⚙ Opções da Partida</h3>
                <button
                  type="button"
                  className="options-close-x"
                  onClick={() => setOptionsOpen(false)}
                  title="Fechar (Esc / B)"
                >
                  ✕
                </button>
              </header>

              <div className="options-modal-body">
                {/* Modo de Jogo */}
                <div
                  className={`options-row ${optionsList[optionsCursor] === 'mode' ? 'options-row-active' : ''}`}
                  onMouseEnter={() => setOptionsCursor(optionsList.indexOf('mode'))}
                >
                  <span className="options-label">Modo:</span>
                  <div className="options-selector">
                    <button
                      type="button"
                      className={`opt-choice-btn ${game.playMode === 'vs_cpu' ? 'active' : ''}`}
                      onClick={() => game.setPlayMode('vs_cpu')}
                    >
                      Contra a CPU
                    </button>
                    <button
                      type="button"
                      className={`opt-choice-btn ${game.playMode === 'hotseat' ? 'active' : ''}`}
                      onClick={() => game.setPlayMode('hotseat')}
                    >
                      2 Jogadores (Hotseat)
                    </button>
                  </div>
                </div>

                {/* Botão de confirmação do controle */}
                <div
                  className="options-row"
                  onMouseEnter={() => setOptionsCursor(-1)}
                >
                  <span className="options-label">Confirmar no controle:</span>
                  <div className="options-selector">
                    {([
                      ['inferior', 'Botão de baixo', 'A no Xbox · ✕ no PlayStation · B no Switch'],
                      ['direita', 'Botão da direita', 'B no Xbox · ○ no PlayStation · A no Switch'],
                    ] as const).map(([value, label, hint]) => (
                      <button
                        key={value}
                        type="button"
                        className={`opt-choice-btn ${confirmButton === value ? 'active' : ''}`}
                        title={hint}
                        onClick={() => {
                          setConfirmButton(value);
                          saveConfirmButton(value);
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dificuldade da CPU */}
                {game.playMode === 'vs_cpu' && (
                  <div
                    className={`options-row ${optionsList[optionsCursor] === 'diff' ? 'options-row-active' : ''}`}
                    onMouseEnter={() => setOptionsCursor(optionsList.indexOf('diff'))}
                  >
                    <span className="options-label">Dificuldade:</span>
                    <div className="options-selector">
                      {(['facil', 'normal', 'dificil'] as const).map((d) => (
                        <button
                          key={d}
                          type="button"
                          className={`opt-choice-btn ${game.difficulty === d ? 'active' : ''}`}
                          onClick={() => game.setDifficulty(d)}
                        >
                          {d === 'facil' ? 'Fácil' : d === 'normal' ? 'Normal' : 'Difícil'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Velocidade da CPU */}
                {game.playMode === 'vs_cpu' && (
                  <div
                    className={`options-row ${optionsList[optionsCursor] === 'speed' ? 'options-row-active' : ''}`}
                    onMouseEnter={() => setOptionsCursor(optionsList.indexOf('speed'))}
                  >
                    <span className="options-label">Velocidade CPU:</span>
                    <div className="options-selector">
                      <button
                        type="button"
                        className={`opt-choice-btn ${game.cpuSpeed === 'normal' ? 'active' : ''}`}
                        onClick={() => game.setCpuSpeed('normal')}
                      >
                        Normal
                      </button>
                      <button
                        type="button"
                        className={`opt-choice-btn ${game.cpuSpeed === 'rapido' ? 'active' : ''}`}
                        onClick={() => game.setCpuSpeed('rapido')}
                      >
                        Rápida
                      </button>
                    </div>
                  </div>
                )}

                {/* Mapa */}
                <div
                  className={`options-row options-row-map ${optionsList[optionsCursor] === 'map' ? 'options-row-active' : ''}`}
                  onMouseEnter={() => setOptionsCursor(optionsList.indexOf('map'))}
                >
                  <span className="options-label">Mapa de batalha:</span>
                  <MapPicker
                    maps={mapList}
                    value={mapId}
                    onChange={(id) => {
                      if (activeCombat) {
                        activeCombat.resolve();
                        setActiveCombat(null);
                      }
                      setActiveDraft(undefined);
                      setMapId(id);
                      setLog([]);
                    }}
                  />
                </div>

                {/* Ações */}
                <div className="options-modal-actions">
                  <button
                    type="button"
                    className={`btn-options-action ${optionsList[optionsCursor] === 'save' ? 'active-action' : ''}`}
                    onMouseEnter={() => setOptionsCursor(optionsList.indexOf('save'))}
                    onClick={() => setSaveMenuOpen(true)}
                    title="Guardar a partida para continuar depois"
                  >
                    💾 Guardar partida
                  </button>

                  <button
                    type="button"
                    className={`btn-options-action restart ${optionsList[optionsCursor] === 'restart' ? 'active-action' : ''}`}
                    onMouseEnter={() => setOptionsCursor(optionsList.indexOf('restart'))}
                    onClick={() => {
                      handleRestart();
                      setOptionsOpen(false);
                    }}
                  >
                    🔄 Recomeçar Partida
                  </button>
                  <button
                    type="button"
                    className={`btn-options-action close ${optionsList[optionsCursor] === 'close' ? 'active-action' : ''}`}
                    onMouseEnter={() => setOptionsCursor(optionsList.indexOf('close'))}
                    onClick={() => setOptionsOpen(false)}
                  >
                    ✕ Fechar e Continuar
                  </button>
                </div>
              </div>

              <div className="options-controls-hint">
                <span>🎮 <b>D-Pad / Analógico</b> navegam · <b>Botão A</b> confirma · <b>B / Start</b> fecha</span>
                <span>⌨ <b>Setas / WASD</b> navegam · <b>Enter</b> confirma · <b>Esc</b> fecha</span>
              </div>
            </div>
          </div>
        )}

        {/* Tela heráldica e perfil completo de Level Up */}
        {levelUpBanner && (
          <div className="levelup-banner-overlay" onClick={dismissLevelUp}>
            <div className="levelup-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="levelup-crown-banner">
                <span>👑</span>
                <span>SUBIU DE NÍVEL!</span>
                <span>⭐</span>
              </div>

              <div className="levelup-hero-profile">
                <div className="levelup-portrait-frame">
                  {manifest && levelUpBanner.portrait ? (
                    <Portrait
                      sheet={levelUpBanner.portrait.sheet}
                      index={levelUpBanner.portrait.index}
                      manifest={manifest}
                    />
                  ) : manifest && levelUpBanner.battler ? (
                    <BattlerPortrait battler={levelUpBanner.battler} manifest={manifest} />
                  ) : (
                    <div className="portrait-fallback" style={{ fontSize: '2rem' }}>⚔️</div>
                  )}
                </div>
                <div className="levelup-hero-meta">
                  <div className="levelup-hero-name">{levelUpBanner.name}</div>
                  <div className="levelup-hero-class">{levelUpBanner.className}</div>
                  <div className="levelup-transition-badge">
                    <span>Nível {levelUpBanner.oldLevel}</span>
                    <span className="levelup-transition-arrow">➔</span>
                    <span className="levelup-new-lvl">Nível {levelUpBanner.newLevel}</span>
                  </div>
                </div>
              </div>

              <div className="levelup-stats-grid">
                {(
                  [
                    ['hp', 'PV'],
                    ['str', 'FOR'],
                    ['mag', 'MAG'],
                    ['skl', 'HAB'],
                    ['spd', 'VEL'],
                    ['lck', 'SOR'],
                    ['def', 'DEF'],
                    ['res', 'RES'],
                  ] as const
                ).map(([key, label], idx) => {
                  const gain = levelUpBanner.gains[key] ?? 0;
                  const increased = gain > 0;
                  const oldVal = levelUpBanner.oldStats[key] ?? 0;
                  const newVal = levelUpBanner.newStats[key] ?? oldVal + gain;
                  return (
                    <div
                      key={key}
                      className={`levelup-stat-cell ${increased ? 'increased' : ''}`}
                      style={{ animationDelay: `${idx * 75}ms` }}
                    >
                      <span className="levelup-stat-badge">{label}</span>
                      <div className="levelup-stat-values">
                        {increased ? (
                          <>
                            <span className="levelup-stat-val-old">{oldVal}</span>
                            <span className="levelup-stat-val-arrow">➔</span>
                            <span className="levelup-stat-val-new">{newVal}</span>
                            <span className="levelup-gain-badge">+{gain}</span>
                          </>
                        ) : (
                          <span className="levelup-stat-val-new">{oldVal}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="levelup-footer-area">
                <button
                  type="button"
                  className="btn-levelup-advance"
                  onClick={dismissLevelUp}
                >
                  Continuar ▶
                </button>
                <div className="levelup-control-tip">
                  <span className="levelup-key-hint">(A) Joystick</span>
                  <span>•</span>
                  <span className="levelup-key-hint">Enter / Espaço</span>
                  <span>•</span>
                  <span>Clique</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controles transparentes na tela (Mobile). Se ativado joystick físico, eles somem. */}
        <VirtualTouchControls
          visible={device.shouldShowTouchControls(gamepad.isConnected)}
          isFullscreen={fullscreen}
          autoBattle={game.autoBattle}
          confirmButton={confirmButton}
          onDirection={(dx, dy) => {
            if (levelUpBanner || bannerActiveRef.current || activeCombat) return;
            if (optionsOpen) {
              if (dy > 0) {
                setOptionsCursor((c) => (c + 1) % optionsList.length);
                audioRef.current?.play('cursor');
              } else if (dy < 0) {
                setOptionsCursor((c) => (c - 1 + optionsList.length) % optionsList.length);
                audioRef.current?.play('cursor');
              } else if (dx !== 0) {
                cycleOptionValue(dx > 0 ? 1 : -1);
              }
              return;
            }
            if (over) {
              if (chapterVerdictOptions.length > 0) {
                const size = chapterVerdictOptions.length;
                if (dy !== 0) {
                  setChapterChoice((c) => (c + (dy > 0 ? 1 : -1) + size) % size);
                  audioRef.current?.play('cursor');
                }
                return;
              }
              if (dy > 0) {
                setVerdictSelection('change_map');
                audioRef.current?.play('cursor');
              } else if (dy < 0) {
                setVerdictSelection('restart');
                audioRef.current?.play('cursor');
              } else if (dx !== 0 && verdictSelection === 'change_map') {
                cycleNextMap(dx > 0 ? 1 : -1);
                audioRef.current?.play('cursor');
              }
              return;
            }
            if ((game.isCpuTurn || game.isCpuActing) && game.selection.mode !== 'campo') return;
            game.moveCursor({ x: dx, y: dy });
          }}
          onConfirm={() => {
            if (cutscene) {
              audioRef.current?.play('confirma');
              const skipBtn = document.querySelector('.cutscene-stage') as HTMLElement | null;
              skipBtn?.click();
              return;
            }
            if (levelUpBanner) {
              audioRef.current?.play('confirma');
              dismissLevelUp();
              return;
            }
            if (bannerActiveRef.current || activeCombat) return;
            audioRef.current?.unlock();
            if (optionsOpen) {
              const currentOpt = optionsList[optionsCursor];
              if (currentOpt === 'save') {
                audioRef.current?.play('confirma');
                setSaveMenuOpen(true);
              } else if (currentOpt === 'restart') {
                audioRef.current?.play('confirma');
                handleRestart();
                setOptionsOpen(false);
              } else if (currentOpt === 'close') {
                audioRef.current?.play('cancela');
                setOptionsOpen(false);
              } else {
                cycleOptionValue(1);
              }
              return;
            }
            if (over) {
              if (chapterVerdictOptions.length > 0) {
                const option = chapterVerdictOptions[chapterChoice % chapterVerdictOptions.length];
                if (option) runChapterChoice(option);
                return;
              }
              audioRef.current?.play('confirma');
              if (verdictSelection === 'restart') {
                handleRestart();
              } else {
                handleChangeMap(selectedNextMapId);
              }
              return;
            }
            if ((game.isCpuTurn || game.isCpuActing) && game.selection.mode !== 'campo') return;
            game.confirm();
          }}
          onCancel={() => {
            if (levelUpBanner) {
              audioRef.current?.play('cancela');
              dismissLevelUp();
              return;
            }
            if (bannerActiveRef.current || activeCombat) return;
            if (game.autoBattle) {
              audioRef.current?.play('cancela');
              game.setAutoBattle(false);
              return;
            }
            if (optionsOpen) {
              audioRef.current?.play('cancela');
              setOptionsOpen(false);
              return;
            }
            if (over) return;
            if (game.isCpuTurn || game.isCpuActing) {
              if (game.selection.mode === 'campo') game.cancel();
              else game.openFieldMenu();
              return;
            }
            if (game.selection.mode === 'trocando') {
              game.finishTrade();
              return;
            }
            game.cancel();
          }}
          onCycleTarget={() => {
            if (bannerActiveRef.current || activeCombat || levelUpBanner || optionsOpen || over) return;
            if (game.isCpuTurn || game.isCpuActing) return;
            game.cycleTarget(1);
            audioRef.current?.play('cursor');
          }}
          onToggleThreat={() => {
            if (bannerActiveRef.current || activeCombat || levelUpBanner || optionsOpen || over) return;
            game.toggleAllThreat();
            audioRef.current?.play('confirma');
          }}
          onMenu={() => {
            if (bannerActiveRef.current) return;
            if (activeCombat) {
              activeCombat.resolve();
              setActiveCombat(null);
              return;
            }
            if (optionsOpen) {
              setOptionsOpen(false);
              return;
            }
            if (over) return;
            if (game.selection.mode === 'campo') game.cancel();
            else game.openFieldMenu();
          }}
          onToggleFullscreen={toggleFullscreen}
          onToggleAutoBattle={() => {
            if (game.isCpuTurn || game.isCpuActing) {
              if (game.autoBattle) game.setAutoBattle(false);
              return;
            }
            game.setAutoBattle(!game.autoBattle);
          }}
        />
      </div>

      <div className="playrow">
        <ol className="battlelog" aria-live="polite">
          {log.slice(0, 8).map((line, i) => <li key={`${i}-${line}`}>{line}</li>)}
        </ol>
      </div>
    </section>
  );
}

const EMPTY_STATE: MatchState = {
  formatVersion: 1, seed: 0, rngState: 0, turn: 1, phase: 'jogador',
  map: { width: 0, height: 0, tiles: [], terrainTypes: [] },
  units: [], objective: { kind: 'derrotar_todos' }, status: 'em_andamento',
  turnOrder: ['jogador', 'inimigo'],
};

const EMPTY_CONTENT: Content = {
  classes: new Map(), weapons: new Map(), items: new Map(), terrain: new Map(),
  triangle: { beats: {}, damageBonus: 0, hitBonus: 0 },
};

export { livingUnits, samePosition };
export type { Vec2 };
